import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Music, Calendar, Settings, BarChart3, 
  Plus, Trash2, Edit, Save, X, Key, Mail, Send, ChevronRight,
  Shield, Eye, EyeOff, CheckCircle, AlertCircle, Inbox, MessageSquare,
  Clock, Check, XCircle, Wallet, Gift, Radio, Bell, CreditCard,
  TrendingUp, DollarSign, Activity, Database, Download, Lock, Unlock, RefreshCw, Globe, Sliders, FileText, Star, Flag, Languages, Zap, Gauge, Scale, Wifi, PiggyBank, Building2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { ContractsManagerPanel } from '../components/dashboard/ContractsManagerPanel';
import { AudioReviewPanel } from '../components/dashboard/AudioReviewPanel';
import { GlobalOpsAssistant } from '../components/dashboard/GlobalOpsAssistant';
import { LiveGodModePanel } from '../components/dashboard/LiveGodModePanel';
import AdCommandCenter from '../components/dashboard/AdCommandCenter';
import LegalReviewPanel from '../components/dashboard/LegalReviewPanel';
import LegalOpsPanel from '../components/dashboard/LegalOpsPanel';
import LegalArchivePanel from '../components/dashboard/LegalArchivePanel';
import AdminOnboarding from '../components/dashboard/AdminOnboarding';
import ModeratorOnboarding from '../components/dashboard/ModeratorOnboarding';
import { API_BASE as API } from '../lib/apiBase';


export default function DashboardPage() {
  const { user, token, isAdmin, isModerator, isDirector, loading: authLoading } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );
  const isOwner = user?.role === 'admin';
  /** Only director account may view/edit API keys (backend enforces). */
  const isSettingsOwner = Boolean(user?.is_settings_owner ?? (isOwner && String(user?.email || '').toLowerCase() === 'pulsestockia@gmail.com'));
  const isModeratorOnly = user?.role === 'moderator';
  const isModerationLead = Boolean(user?.is_moderation_lead);
  const [activeTab, setActiveTab] = useState('overview');
  const [artists, setArtists] = useState([]);
  const [events, setEvents] = useState([]);
  const [songs, setSongs] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [inquireOffers, setInquireOffers] = useState([]);
  const [settings, setSettings] = useState({});
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [showKeys, setShowKeys] = useState({});
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'user', name: '' });
  const [inviteResult, setInviteResult] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Pluka inbox
  const [plukaInbox, setPlukaInbox] = useState({ pending: [], confirmed: [], cancelled: [], total_pending: 0 });
  const [plukaTab, setPlukaTab] = useState('pending');
  const [plukaChat, setPlukaChat] = useState('');
  const [plukaChatReply, setPlukaChatReply] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [replyMsg, setReplyMsg] = useState('');
  const [bookingMessages, setBookingMessages] = useState([]);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [plukaPrice, setPlukaPrice] = useState('');
  const [messageTranslations, setMessageTranslations] = useState({});
  const [bookingContractTemplateId, setBookingContractTemplateId] = useState('');
  
  // Admin: Wallets, Transactions, Donations, Notifications
  const [adminWallets, setAdminWallets] = useState([]);
  const [adminTransactions, setAdminTransactions] = useState([]);
  const [adminDonations, setAdminDonations] = useState({ donations: [], total: 0, total_fees: 0 });
  const [adminLiveSessions, setAdminLiveSessions] = useState([]);
  const [notifForm, setNotifForm] = useState({ user_id: 'all', title: '', message: '' });
  const [notifSent, setNotifSent] = useState(false);
  const [escrowData, setEscrowData] = useState({ frozen: [], released: [], total_frozen: 0, total_released: 0, total_platform_earned: 0 });
  
  // Advanced admin analytics
  const [financialData, setFinancialData] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [contentAnalytics, setContentAnalytics] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [systemConfig, setSystemConfig] = useState(null);
  const [financialPeriod, setFinancialPeriod] = useState('30d');

  // Pluka Enhanced
  const [plukaAiMsg, setPlukaAiMsg] = useState('');
  const [plukaAiChat, setPlukaAiChat] = useState([]);
  const [plukaAiLoading, setPlukaAiLoading] = useState(false);
  const [plukaAiPendingAction, setPlukaAiPendingAction] = useState(null);
  const [plukaAiPassword, setPlukaAiPassword] = useState('');
  const [plukaAiActionLoading, setPlukaAiActionLoading] = useState(false);
  const [plukaSubTab, setPlukaSubTab] = useState('inbox');
  const [plukaAllChats, setPlukaAllChats] = useState([]);
  const [plukaFeatured, setPlukaFeatured] = useState({ featured: [], all_artists: [], featured_ids: [] });
  const [plukaCalendar, setPlukaCalendar] = useState({ bookings: [], events: [] });
  const [bulkSelected, setBulkSelected] = useState([]);
  const [contentReports, setContentReports] = useState({ reports: [], stats: {} });
  const [contractTemplates, setContractTemplates] = useState([]);
  const [audioReviewQueue, setAudioReviewQueue] = useState({ songs: [], stats: {} });
  const [audioReviewBusy, setAudioReviewBusy] = useState('');

  // Command Center
  const [ccSummary, setCcSummary] = useState(null);
  const [ccProviders, setCcProviders] = useState([]);
  const [ccActivity, setCcActivity] = useState([]);
  const [ccErrors, setCcErrors] = useState([]);
  const [ccEconomy, setCcEconomy] = useState(null);
  const [ccLoading, setCcLoading] = useState(false);

  // API Health Monitor
  const [apiHealth, setApiHealth] = useState(null);
  const [apiHealthLoading, setApiHealthLoading] = useState(false);

  // Platform Financials (Balance)
  const [platformFinancials, setPlatformFinancials] = useState(null);
  const [pfLoading, setPfLoading] = useState(false);
  const [pfPeriod, setPfPeriod] = useState('30d');

  // Artist form
  const [editingArtist, setEditingArtist] = useState(null);
  const [artistForm, setArtistForm] = useState({ name: '', genre: '', bio_es: '', bio_en: '', photo: '', cover: '', price_per_show: 0, city: '' });

  // Song form
  const [songForm, setSongForm] = useState({ title_es: '', title_en: '', artist_id: '', artist_name: '', duration: 0, audio_url: '', cover: '', genre: '', isrc: '' });
  const [showSongForm, setShowSongForm] = useState(false);

  // Event form
  const [eventForm, setEventForm] = useState({ title_es: '', title_en: '', description_es: '', date: '', genre: '', price: 0, image: '', capacity: 0, location: { lat: 40.4168, lng: -3.7038, name: '', venue: '' } });
  const [showEventForm, setShowEventForm] = useState(false);

  const [showAdminOnboarding, setShowAdminOnboarding] = useState(() => {
    if (user?.role === 'pluka' && !localStorage.getItem('pulse_admin_onboarding_done')) return true;
    return false;
  });
  const [showModeratorOnboarding, setShowModeratorOnboarding] = useState(() => {
    if (user?.role === 'moderator' && !localStorage.getItem('pulse_moderator_onboarding_done')) return true;
    return false;
  });
  const [moderatorApproved, setModeratorApproved] = useState(null);
  const [modInviteEmail, setModInviteEmail] = useState('');
  const [modInviteName, setModInviteName] = useState('');
  const [modInviteNote, setModInviteNote] = useState('');
  const [modInviteBusy, setModInviteBusy] = useState(false);
  const [modInviteMsg, setModInviteMsg] = useState(null);
  const [modInvitations, setModInvitations] = useState([]);
  const [modRequests, setModRequests] = useState([]);
  const isPluka = user?.role === 'pluka';
  const [compliance, setCompliance] = useState(null);

  const fetchAll = async () => {
    try {
      const [a, e, s, u, b, st, an, inbox, ct, of] = await Promise.all([
        fetch(`${API}/api/artists?limit=50`, { headers }).then(r => r.json()),
        fetch(`${API}/api/events?limit=50`, { headers }).then(r => r.json()),
        fetch(`${API}/api/songs?limit=50`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/users`, { headers }).then(r => r.json()),
        fetch(`${API}/api/bookings`, { headers }).then(r => r.json()),
        fetch(`${API}/api/admin/settings`, { headers }).then(r => r.json()),
        fetch(`${API}/api/analytics/overview`, { headers }).then(r => r.json()),
        fetch(`${API}/api/pluka/inbox`, { headers }).then(r => r.json()).catch(() => ({ pending: [], confirmed: [], cancelled: [], total_pending: 0 })),
        fetch(`${API}/api/contracts/templates`, { headers }).then(r => r.json()).catch(() => ({ templates: [] })),
        fetch(`${API}/api/booking-offers?role=admin&limit=200`, { headers }).then(r => r.json()).catch(() => ({ offers: [] })),
      ]);
      setArtists(a.artists || []);
      setEvents(e.events || []);
      setSongs(s.songs || []);
      setUsers(u.users || []);
      setBookings(b.bookings || []);
      setInquireOffers(of.offers || []);
      setSettings(st || {});
      setSettingsForm(st || {});
      setAnalytics(an || {});
      setPlukaInbox(inbox);
      setContractTemplates(ct.templates || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    if (authLoading || isModeratorOnly) return;
    if (!isOwner && !isPluka) return;
    const loadCompliance = async () => {
      try {
        const r = await fetch(`${API}/api/legal/compliance/summary`, { headers });
        if (r.ok) setCompliance(await r.json());
      } catch (e) {
        console.error(e);
      }
    };
    loadCompliance();
  }, [authLoading, isOwner, isPluka, isModeratorOnly, token, headers]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin && !isModerator) { navigate('/'); return; }
    if (!isModeratorOnly) fetchAll();
  }, [isAdmin, isModerator, authLoading, isModeratorOnly]);

  useEffect(() => {
    if (user?.role !== 'moderator' || !token) return;
    fetch(`${API}/api/moderator/approval-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setModeratorApproved(data.approved === true))
      .catch(() => setModeratorApproved(false));
  }, [user?.role, token]);

  const fetchModInvitations = async () => {
    if (!token || !user?.is_moderation_lead) return;
    try {
      const r = await fetch(`${API}/api/moderator/invitations`, { headers });
      const d = await r.json();
      setModInvitations(d.invitations || []);
    } catch { setModInvitations([]); }
  };

  const fetchModRequests = async () => {
    if (!token || (!isOwner && !isPluka)) return;
    try {
      const r = await fetch(`${API}/api/admin/moderator-requests`, { headers });
      const d = await r.json();
      setModRequests(d.requests || []);
    } catch { setModRequests([]); }
  };

  useEffect(() => {
    if (activeTab === 'mod_team' && user?.is_moderation_lead) fetchModInvitations();
  }, [activeTab, user?.is_moderation_lead, token]);

  useEffect(() => {
    if (activeTab === 'users' && (isOwner || isPluka)) fetchModRequests();
  }, [activeTab, isOwner, isPluka, token]);

  const saveSettings = async () => {
    if (!isSettingsOwner) return;
    setSaving(true);
    const payload = { ...settingsForm };
    if (typeof payload.ops_sentinel_openai_fallback_enabled === 'string') {
      const lowered = payload.ops_sentinel_openai_fallback_enabled.trim().toLowerCase();
      payload.ops_sentinel_openai_fallback_enabled = !(lowered === 'false' || lowered === '0' || lowered === 'no');
    }
    await fetch(`${API}/api/admin/settings`, {
      method: 'PUT', headers,
      body: JSON.stringify(payload)
    });
    setSaving(false);
    fetchAll();
  };

  const updateVerificationWeight = (key, value) => {
    setSettingsForm((current) => ({
      ...current,
      song_verification_weights: {
        ...(current.song_verification_weights || {}),
        [key]: value,
      },
    }));
  };

  const updateVerificationThreshold = (key, value) => {
    setSettingsForm((current) => ({
      ...current,
      song_verification_thresholds: {
        ...(current.song_verification_thresholds || {}),
        [key]: value,
      },
    }));
  };

  const fetchAudioReviewQueue = async () => {
    try {
      const data = await fetch(`${API}/api/verification/review-queue`, { headers }).then((r) => r.json());
      setAudioReviewQueue(data || { songs: [], stats: {} });
    } catch (e) {
      setAudioReviewQueue({ songs: [], stats: {} });
    }
  };

  const updateSongVerificationDecision = async (songId, status) => {
    setAudioReviewBusy(songId);
    try {
      await fetch(`${API}/api/verification/songs/${songId}/decision`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ status }),
      });
      await Promise.all([fetchAll(), fetchAudioReviewQueue()]);
    } finally {
      setAudioReviewBusy('');
    }
  };

  const rerunSongVerification = async (songId) => {
    setAudioReviewBusy(songId);
    try {
      await fetch(`${API}/api/verification/songs/${songId}/rerun`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'manual_dashboard_rerun' }),
      });
      await Promise.all([fetchAll(), fetchAudioReviewQueue()]);
    } finally {
      setAudioReviewBusy('');
    }
  };

  const inviteUser = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API}/api/admin/invite`, {
        method: 'POST', headers,
        body: JSON.stringify({ ...inviteForm, app_origin: window.location.origin })
      });
      const data = await res.json();
      setInviteResult(data);
      setInviteForm({ email: '', role: 'user', name: '' });
      fetchAll();
    } catch (e) { console.error(e); }
  };

  const deleteUser = async (uid) => {
    await fetch(`${API}/api/admin/users/${uid}`, { method: 'DELETE', headers });
    fetchAll();
  };

  const updateUserRole = async (uid, role) => {
    await fetch(`${API}/api/admin/users/${uid}/role`, {
      method: 'PUT', headers, body: JSON.stringify({ role })
    });
    fetchAll();
  };

  const deleteArtist = async (aid) => {
    await fetch(`${API}/api/artists/${aid}`, { method: 'DELETE', headers });
    fetchAll();
  };

  const saveArtist = async () => {
    const body = { ...artistForm, genre: artistForm.genre.split(',').map(g => g.trim()).filter(Boolean) };
    if (editingArtist) {
      await fetch(`${API}/api/artists/${editingArtist}`, { method: 'PUT', headers, body: JSON.stringify(body) });
    } else {
      await fetch(`${API}/api/artists`, { method: 'POST', headers, body: JSON.stringify(body) });
    }
    setEditingArtist(null);
    setArtistForm({ name: '', genre: '', bio_es: '', bio_en: '', photo: '', cover: '', price_per_show: 0, city: '' });
    fetchAll();
  };

  const saveSong = async () => {
    await fetch(`${API}/api/songs`, { method: 'POST', headers, body: JSON.stringify(songForm) });
    setShowSongForm(false);
    setSongForm({ title_es: '', title_en: '', artist_id: '', artist_name: '', duration: 0, audio_url: '', cover: '', genre: '', isrc: '' });
    fetchAll();
  };

  const deleteSong = async (sid) => {
    await fetch(`${API}/api/songs/${sid}`, { method: 'DELETE', headers });
    fetchAll();
  };

  const saveEvent = async () => {
    await fetch(`${API}/api/events`, { method: 'POST', headers, body: JSON.stringify(eventForm) });
    setShowEventForm(false);
    fetchAll();
  };

  const deleteEvent = async (eid) => {
    await fetch(`${API}/api/events/${eid}`, { method: 'DELETE', headers });
    fetchAll();
  };

  const updateBookingStatus = async (bid, status) => {
    await fetch(`${API}/api/bookings/${bid}/status`, {
      method: 'PUT', headers, body: JSON.stringify({ status })
    });
    fetchAll();
  };

  const openBookingDetail = async (booking) => {
    setSelectedBooking(booking);
    try {
      const res = await fetch(`${API}/api/bookings/${booking.id}`, { headers });
      const data = await res.json();
      setSelectedBooking(data);
      setBookingMessages(data.messages || []);
      const bookingTemplates = (contractTemplates || []).filter(item => item.category === 'booking');
      if (bookingTemplates[0]?.id) setBookingContractTemplateId(bookingTemplates[0].id);
    } catch (e) { setBookingMessages([]); }
  };

  const translateOpsText = async (text, translationKey) => {
    if (!text?.trim()) return;
    const data = await fetch(`${API}/api/pluka/translate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text, source_language: 'English', target_language: 'Spanish' }),
    }).then(r => r.json());
    setMessageTranslations(prev => ({ ...prev, [translationKey]: data.translated_text || '' }));
  };

  const translateReplyToEnglish = async () => {
    if (!replyMsg.trim()) return;
    const data = await fetch(`${API}/api/pluka/translate`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ text: replyMsg, source_language: 'Spanish', target_language: 'English' }),
    }).then(r => r.json());
    setReplyMsg(data.translated_text || replyMsg);
  };

  const assignBookingContract = async () => {
    if (!selectedBooking?.id || !bookingContractTemplateId) return;
    await fetch(`${API}/api/contracts/instances`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ template_id: bookingContractTemplateId, booking_id: selectedBooking.id, user_id: selectedBooking.user_id, artist_id: selectedBooking.artist_id }),
    });
    openBookingDetail(selectedBooking);
  };

  const sendBookingReply = async () => {
    if (!replyMsg.trim() || !selectedBooking) return;
    setSendingMsg(true);
    try {
      await fetch(`${API}/api/bookings/${selectedBooking.id}/message`, {
        method: 'POST', headers,
        body: JSON.stringify({ message: replyMsg })
      });
      setReplyMsg('');
      openBookingDetail(selectedBooking);
    } catch (e) {}
    setSendingMsg(false);
  };

  const updateBookingPrice = async (bid, newPrice) => {
    await fetch(`${API}/api/bookings/${bid}/status`, {
      method: 'PUT', headers, body: JSON.stringify({ status: 'pending', price: parseFloat(newPrice) })
    });
    fetchAll();
  };

  const fetchAdminTab = async (tab) => {
    try {
      if (tab === 'wallets' && adminWallets.length === 0) {
        const data = await fetch(`${API}/api/admin/wallets`, { headers }).then(r => r.json());
        setAdminWallets(data.wallets || []);
      }
      if (tab === 'donations') {
        const data = await fetch(`${API}/api/admin/apoyos`, { headers }).then(r => r.json());
        setAdminDonations(data);
      }
      if (tab === 'live') {
        const data = await fetch(`${API}/api/admin/live-sessions`, { headers }).then(r => r.json());
        setAdminLiveSessions(data.sessions || []);
      }
      if (tab === 'escrow') {
        const data = await fetch(`${API}/api/admin/escrow`, { headers }).then(r => r.json());
        setEscrowData(data);
      }
      if (tab === 'financials') {
        const data = await fetch(`${API}/api/admin/analytics/financial?period=${financialPeriod}`, { headers }).then(r => r.json());
        setFinancialData(data);
      }
      if (tab === 'user_analytics') {
        const data = await fetch(`${API}/api/admin/analytics/users`, { headers }).then(r => r.json());
        setUserAnalytics(data);
      }
      if (tab === 'content_analytics') {
        const data = await fetch(`${API}/api/admin/analytics/content`, { headers }).then(r => r.json());
        setContentAnalytics(data);
      }
      if (tab === 'audit') {
        const data = await fetch(`${API}/api/admin/audit`, { headers }).then(r => r.json());
        setAuditLogs(data.logs || []);
      }
      if (tab === 'system' && !systemConfig) {
        const data = await fetch(`${API}/api/admin/system`, { headers }).then(r => r.json());
        setSystemConfig(data);
      }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (['wallets', 'donations', 'live', 'escrow', 'financials', 'user_analytics', 'content_analytics', 'audit', 'system'].includes(activeTab)) fetchAdminTab(activeTab);
    if (activeTab === 'pluka') fetchPlukaData();
    if (activeTab === 'reports') fetchReports();
    if (activeTab === 'command_center') fetchCommandCenter();
    if (activeTab === 'api_health') fetchApiHealth();
    if (activeTab === 'platform_balance') fetchPlatformFinancials();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeTab, financialPeriod, plukaSubTab, pfPeriod]);

  const fetchReports = async () => {
    try {
      const r = await fetch(`${API}/api/admin/reports`, { headers });
      setContentReports(await r.json());
    } catch {}
  };

  const fetchCommandCenter = async () => {
    setCcLoading(true);
    try {
      const [sumR, provR, actR, errR, ecoR] = await Promise.all([
        fetch(`${API}/api/command-center/summary?days=7`, { headers }),
        fetch(`${API}/api/command-center/providers`, { headers }),
        fetch(`${API}/api/command-center/activity?hours=24`, { headers }),
        fetch(`${API}/api/command-center/errors?limit=10`, { headers }),
        fetch(`${API}/api/command-center/economy`, { headers }),
      ]);
      setCcSummary(await sumR.json());
      const provData = await provR.json();
      setCcProviders(provData.providers || []);
      const actData = await actR.json();
      setCcActivity(actData.activity || []);
      const errData = await errR.json();
      setCcErrors(errData.errors || []);
      setCcEconomy(await ecoR.json());
    } catch (e) { console.error('Command Center fetch error:', e); }
    setCcLoading(false);
  };

  const fetchApiHealth = async () => {
    setApiHealthLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/api-health/status`, { headers });
      setApiHealth(await r.json());
    } catch (e) { console.error('API Health fetch error:', e); }
    setApiHealthLoading(false);
  };

  const fetchPlatformFinancials = async () => {
    setPfLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/platform-financials/overview?period=${pfPeriod}`, { headers });
      setPlatformFinancials(await r.json());
    } catch (e) { console.error('Platform Financials fetch error:', e); }
    setPfLoading(false);
  };

  const fetchPlukaData = async () => {
    try {
      if (plukaSubTab === 'chats') {
        const r = await fetch(`${API}/api/pluka/all-chats`, { headers });
        const d = await r.json();
        setPlukaAllChats(d.chats || []);
      } else if (plukaSubTab === 'ai') {
        const r = await fetch(`${API}/api/pluka/ai-assistant/history`, { headers });
        const d = await r.json();
        setPlukaAiChat(d.messages || []);
        setPlukaAiPendingAction(d.pending_action || null);
      } else if (plukaSubTab === 'featured') {
        const r = await fetch(`${API}/api/pluka/featured-artists`, { headers });
        const d = await r.json();
        setPlukaFeatured(d);
      } else if (plukaSubTab === 'calendar') {
        const r = await fetch(`${API}/api/pluka/calendar`, { headers });
        const d = await r.json();
        setPlukaCalendar(d);
      } else if (plukaSubTab === 'audio-review') {
        await fetchAudioReviewQueue();
      }
    } catch (e) {}
  };

  const sendPlukaAi = async () => {
    if (!plukaAiMsg.trim()) return;
    const userMsg = plukaAiMsg.trim();
    setPlukaAiChat(prev => [...prev, { role: 'user', content: userMsg }]);
    setPlukaAiMsg('');
    setPlukaAiLoading(true);
    try {
      const res = await fetch(`${API}/api/pluka/ai-assistant`, {
        method: 'POST', headers, body: JSON.stringify({ message: userMsg, conversation: plukaAiChat })
      });
      const data = await res.json();
      setPlukaAiChat(prev => [...prev, { role: 'assistant', content: data.response }]);
      setPlukaAiPendingAction(data.pending_action || null);
    } catch { setPlukaAiChat(prev => [...prev, { role: 'assistant', content: 'Error de conexion' }]); }
    setPlukaAiLoading(false);
  };

  const confirmPlukaAiAction = async () => {
    if (!plukaAiPendingAction?.id || !plukaAiPassword.trim()) return;
    setPlukaAiActionLoading(true);
    try {
      const res = await fetch(`${API}/api/pluka/ai-assistant`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ action_id: plukaAiPendingAction.id, confirmation_password: plukaAiPassword })
      });
      const data = await res.json();
      setPlukaAiChat(prev => [...prev, { role: 'assistant', content: data.response }]);
      setPlukaAiPendingAction(data.pending_action || null);
      if (!data.pending_action) {
        setPlukaAiPassword('');
        fetchAll();
        fetchPlukaData();
      }
    } catch {
      setPlukaAiChat(prev => [...prev, { role: 'assistant', content: 'No he podido ejecutar la accion ahora mismo.' }]);
    }
    setPlukaAiActionLoading(false);
  };

  const toggleFeaturedArtist = async (artistId) => {
    const current = plukaFeatured.featured_ids || [];
    const newIds = current.includes(artistId) ? current.filter(id => id !== artistId) : [...current, artistId];
    await fetch(`${API}/api/pluka/featured-artists`, { method: 'PUT', headers, body: JSON.stringify({ artist_ids: newIds }) });
    fetchPlukaData();
  };

  const plukaReply = async (bookingId, message) => {
    if (!message.trim()) return;
    await fetch(`${API}/api/pluka/reply/${bookingId}`, { method: 'POST', headers, body: JSON.stringify({ message }) });
    fetchPlukaData();
  };

  const bulkAction = async (action) => {
    if (bulkSelected.length === 0) return;
    await fetch(`${API}/api/pluka/bulk-action`, { method: 'POST', headers, body: JSON.stringify({ booking_ids: bulkSelected, action }) });
    setBulkSelected([]);
    fetchAll();
  };

  const sendAdminNotif = async () => {
    if (!notifForm.title || !notifForm.message) return;
    await fetch(`${API}/api/admin/notifications/send`, {
      method: 'POST', headers, body: JSON.stringify(notifForm)
    });
    setNotifSent(true);
    setTimeout(() => setNotifSent(false), 3000);
  };

  const MODERATOR_TABS = [
    'overview',
    ...(isModerationLead ? ['mod_team'] : []),
    'songs', 'reports', 'live', 'god_mode', 'notifications',
  ];

  const allTabs = [
    { id: 'overview', icon: LayoutDashboard, label: t('dashboard.overview') },
    ...(isModerationLead ? [{ id: 'mod_team', icon: Shield, label: lang === 'es' ? 'Equipo moderación' : 'Moderation team' }] : []),
    { id: 'god_mode', icon: Shield, label: lang === 'es' ? 'Control Live' : 'Live Control', highlight: true },
    { id: 'financials', icon: DollarSign, label: lang === 'es' ? 'Finanzas' : 'Financials' },
    { id: 'user_analytics', icon: Activity, label: lang === 'es' ? 'Usuarios' : 'Users Intel' },
    { id: 'content_analytics', icon: TrendingUp, label: lang === 'es' ? 'Contenido' : 'Content' },
    { id: 'pluka', icon: Inbox, label: lang === 'es' ? 'Operaciones' : 'Operations' },
    { id: 'artists', icon: Music, label: t('dashboard.artists') },
    { id: 'songs', icon: Music, label: t('dashboard.songs') },
    { id: 'events', icon: Calendar, label: t('dashboard.events') },
    { id: 'users', icon: Users, label: lang === 'es' ? 'Cuentas' : 'Accounts' },
    { id: 'bookings', icon: Calendar, label: t('dashboard.bookings') },
    { id: 'wallets', icon: Wallet, label: lang === 'es' ? 'Monederos' : 'Wallets' },
    { id: 'donations', icon: Gift, label: lang === 'es' ? 'Apoyos' : 'Supports' },
    { id: 'live', icon: Radio, label: 'Live' },
    { id: 'escrow', icon: CreditCard, label: 'Escrow' },
    { id: 'notifications', icon: Bell, label: lang === 'es' ? 'Notifs' : 'Notifs' },
    ...(isOwner ? [{ id: 'ads', icon: Zap, label: lang === 'es' ? 'Publicidad' : 'Ads' }] : []),
    ...(isOwner ? [{ id: 'legal_review', icon: Scale, label: lang === 'es' ? 'Legal' : 'Legal' }] : []),
    ...(isOwner ? [{ id: 'legal_ops', icon: FileText, label: 'Legal Ops' }] : []),
    ...(isOwner ? [{ id: 'legal_archive', icon: FileText, label: lang === 'es' ? 'Archivo Legal' : 'Legal Archive' }] : []),
    ...(isOwner ? [{ id: 'command_center', icon: Gauge, label: lang === 'es' ? 'Command Center' : 'Command Center' }] : []),
    ...(isOwner ? [{ id: 'api_health', icon: Wifi, label: lang === 'es' ? 'Estado APIs' : 'API Health' }] : []),
    ...(isOwner ? [{ id: 'platform_balance', icon: PiggyBank, label: lang === 'es' ? 'Balance' : 'Balance' }] : []),
    ...(isOwner ? [{ id: 'system', icon: Sliders, label: lang === 'es' ? 'Sistema' : 'System' }] : []),
    ...(isOwner ? [{ id: 'audit', icon: FileText, label: lang === 'es' ? 'Auditoria' : 'Audit' }] : []),
    { id: 'reports', icon: Flag, label: lang === 'es' ? 'Reportes' : 'Reports' },
    ...(isSettingsOwner ? [{ id: 'settings', icon: Settings, label: t('dashboard.settings') }] : []),
  ];

  const tabs = isModeratorOnly ? allTabs.filter(t => MODERATOR_TABS.includes(t.id)) : allTabs;

  const launchChecklist = [
    { id: 'owner-access', label: lang === 'es' ? 'Acceso propietario activado' : 'Owner access activated', done: users.some(u => u.email === 'pulsestockia@gmail.com' && !u.must_set_password && u.activation_completed) },
    { id: 'ops-access', label: lang === 'es' ? 'Acceso de operaciones activado' : 'Operations access activated', done: users.some(u => u.email === 'alan.inca6@gmail.com' && !u.must_set_password && u.activation_completed) },
    { id: 'sendgrid', label: 'SendGrid', done: Boolean(settings.sendgrid_api_key && settings.sendgrid_sender_email) },
    { id: 'stripe', label: 'Stripe', done: Boolean(settings.stripe_publishable_key && settings.stripe_secret_key) },
    { id: 'livekit', label: 'LiveKit', done: Boolean(settings.livekit_api_key && settings.livekit_api_secret && settings.livekit_url) },
    { id: 'ai', label: lang === 'es' ? 'IA de soporte' : 'Support AI', done: Boolean(settings.openai_key || settings.claude_key) },
    { id: 'ownership', label: lang === 'es' ? 'Ownership verification' : 'Ownership verification', done: Boolean(settings.ACRCLOUD_HOST && settings.ACRCLOUD_ACCESS_KEY && settings.ACRCLOUD_ACCESS_SECRET) },
  ];

  const formatCalendarLocation = (location) => {
    if (!location) return '-';
    if (typeof location === 'string') return location;
    const composed = [location.venue, location.name].filter(Boolean).join(' · ');
    if (composed) return composed;
    if (typeof location.lat === 'number' && typeof location.lng === 'number') {
      return `${location.lat}, ${location.lng}`;
    }
    return '-';
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div data-testid="dashboard-page" className="min-h-screen flex">
      {showAdminOnboarding && (
        <AdminOnboarding lang={lang} onComplete={() => setShowAdminOnboarding(false)} />
      )}
      {showModeratorOnboarding && (
        <ModeratorOnboarding
          token={token}
          lang={lang}
          userName={user?.name}
          isModerationLead={isModerationLead}
          onComplete={(status) => {
            setShowModeratorOnboarding(false);
            if (status === 'active') {
              setModeratorApproved(true);
            } else if (status === 'pending_approval') {
              setModeratorApproved(false);
            }
          }}
        />
      )}
      {isModeratorOnly && moderatorApproved === false && !showModeratorOnboarding && (
        <div className="fixed inset-0 z-[190] bg-black/90 flex items-center justify-center p-4">
          <div className="bg-[#0A0A0B] border border-[#27272A] rounded-2xl w-full max-w-md p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4">
              <Clock size={28} className="text-amber-400" />
            </div>
            <h2 className="font-heading font-bold text-xl mb-2">
              {lang === 'es' ? 'Pendiente de aprobacion' : 'Pending approval'}
            </h2>
            <p className="text-sm text-pulse-muted leading-relaxed mb-4">
              {lang === 'es'
                ? 'Tu acuerdo de moderador ha sido enviado al equipo directivo. Recibiras acceso al panel de moderacion cuando sea aprobado. Esto suele tardar menos de 24 horas.'
                : 'Your moderator agreement has been sent to the leadership team. You will receive access to the moderation panel once approved. This usually takes less than 24 hours.'}
            </p>
            <p className="text-[10px] text-pulse-muted">
              {lang === 'es' ? 'Puedes cerrar esta ventana. Te notificaremos cuando tu acceso este listo.' : 'You can close this window. We will notify you when your access is ready.'}
            </p>
            <button onClick={() => navigate('/')} className="mt-4 px-6 py-2 bg-pulse-surface border border-pulse-border rounded-xl text-sm hover:bg-pulse-elevated transition-colors">
              {lang === 'es' ? 'Volver al inicio' : 'Back to home'}
            </button>
          </div>
        </div>
      )}
      {/* Sidebar */}
      <aside className="w-56 bg-pulse-surface border-r border-pulse-border min-h-screen p-4 hidden md:block">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <img src="/assets/logo.png" alt="PulseMusic" className="w-8 h-8 object-contain" style={{background:'transparent'}} />
          <span className="font-heading font-bold text-sm">PulseMusic</span>
        </Link>
        <nav className="space-y-1">
          {isDirector && (
            <Link
              to="/director/departamentos"
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-amber-200/95 border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/15 mb-2"
            >
              <Building2 size={16} />
              {lang === 'es' ? 'Direccion · departamentos' : 'Leadership · departments'}
            </Link>
          )}
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`dash-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors duration-200 ${
                activeTab === tab.id 
                  ? 'bg-pulse-primary text-black font-semibold' 
                  : tab.highlight 
                    ? 'text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/20' 
                    : 'text-pulse-muted hover:text-white hover:bg-pulse-elevated'
              }`}
            >
              <tab.icon size={16} className={tab.highlight && activeTab !== tab.id ? 'text-red-500' : ''} />
              {tab.label}
              {tab.highlight && activeTab !== tab.id && (
                <span className="ml-auto w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              )}
            </button>
          ))}
        </nav>
      </aside>

      {/* Mobile tabs */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-pulse-surface border-b border-pulse-border">
        {isDirector && (
          <div className="px-2 pt-2">
            <Link
              to="/director/departamentos"
              className="block w-full text-center text-xs font-bold py-2 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-200"
            >
              {lang === 'es' ? 'Direccion · departamentos internos' : 'Leadership · internal departments'}
            </Link>
          </div>
        )}
        <div className="p-2 border-b border-pulse-border/60">
          <select
            data-testid="mobile-dash-tab-select"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-3 py-2.5 text-sm focus:ring-1 focus:ring-cyan-500 focus:outline-none"
          >
            {tabs.map(tab => <option key={tab.id} value={tab.id}>{tab.label}</option>)}
          </select>
        </div>
        <div className="flex p-2 gap-1 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs whitespace-nowrap ${
                activeTab === tab.id ? 'bg-pulse-primary text-black font-semibold' : 'text-pulse-muted'
              }`}
            >
              <tab.icon size={12} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 p-4 md:p-8 pt-16 md:pt-8 pb-32 md:pb-28 overflow-y-auto">
        {/* Overview */}
        {activeTab === 'mod_team' && isModerationLead && (
          <div data-testid="dash-mod-team" className="max-w-xl">
            <h2 className="font-heading font-bold text-2xl mb-2">{lang === 'es' ? 'Tu equipo de confianza' : 'Your trusted team'}</h2>
            <p className="text-sm text-pulse-muted mb-6">
              {lang === 'es'
                ? 'Propón candidatos al equipo de moderación. Ellos recibirán el mismo onboarding legal; tú y dirección validáis el archivo.'
                : 'Propose candidates for the moderation team. They get the same legal onboarding; you and leadership validate the file.'}
            </p>
            <div className="bg-pulse-surface border border-pulse-border rounded-xl p-5 space-y-3 mb-6">
              <input
                type="email"
                value={modInviteEmail}
                onChange={e => setModInviteEmail(e.target.value)}
                placeholder={lang === 'es' ? 'Email del candidato' : 'Candidate email'}
                className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm"
              />
              <input
                type="text"
                value={modInviteName}
                onChange={e => setModInviteName(e.target.value)}
                placeholder={lang === 'es' ? 'Nombre (opcional)' : 'Name (optional)'}
                className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm"
              />
              <textarea
                value={modInviteNote}
                onChange={e => setModInviteNote(e.target.value)}
                placeholder={lang === 'es' ? 'Nota interna para dirección (opcional)' : 'Internal note for leadership (optional)'}
                rows={2}
                className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm resize-none"
              />
              <button
                type="button"
                disabled={modInviteBusy || !modInviteEmail.trim()}
                onClick={async () => {
                  setModInviteBusy(true);
                  setModInviteMsg(null);
                  try {
                    const r = await fetch(`${API}/api/moderator/invite-candidate`, {
                      method: 'POST',
                      headers,
                      body: JSON.stringify({
                        email: modInviteEmail.trim(),
                        name: modInviteName.trim(),
                        note: modInviteNote.trim(),
                        app_origin: typeof window !== 'undefined' ? window.location.origin : '',
                      }),
                    });
                    const d = await r.json();
                    if (r.ok) {
                      setModInviteMsg({ ok: true, text: d.message || 'OK' });
                      setModInviteEmail('');
                      setModInviteName('');
                      setModInviteNote('');
                      fetchModInvitations();
                    } else {
                      setModInviteMsg({ ok: false, text: d.detail || 'Error' });
                    }
                  } catch {
                    setModInviteMsg({ ok: false, text: 'Error' });
                  }
                  setModInviteBusy(false);
                }}
                className="w-full bg-pulse-primary text-black font-bold py-3 rounded-xl text-sm disabled:opacity-50"
              >
                {modInviteBusy ? '…' : (lang === 'es' ? 'Enviar invitación' : 'Send invitation')}
              </button>
              {modInviteMsg && (
                <p className={`text-xs ${modInviteMsg.ok ? 'text-green-400' : 'text-red-400'}`}>{modInviteMsg.text}</p>
              )}
            </div>
            <h3 className="font-heading font-semibold text-sm mb-2 text-pulse-muted">{lang === 'es' ? 'Invitaciones recientes' : 'Recent invitations'}</h3>
            <div className="space-y-2">
              {modInvitations.length === 0 ? (
                <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Ninguna aún.' : 'None yet.'}</p>
              ) : (
                modInvitations.map((inv) => (
                  <div key={inv.email} className="bg-pulse-elevated/50 border border-pulse-border rounded-lg px-3 py-2 text-xs">
                    <span className="text-white font-medium">{inv.email}</span>
                    <span className="text-pulse-muted ml-2">{inv.invited_at ? new Date(inv.invited_at).toLocaleString() : ''}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'overview' && (
          <div data-testid="dash-overview">
            <h2 className="font-heading font-bold text-2xl mb-6">{t('dashboard.overview')}</h2>
            {(isOwner || isPluka) && (
              <div
                className="bg-gradient-to-br from-cyan-950/40 to-pulse-surface border border-cyan-500/20 rounded-2xl p-5 mb-6"
                data-testid="compliance-briefing-card"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div>
                    <h3 className="font-heading font-semibold text-base text-white flex items-center gap-2">
                      <Scale size={18} className="text-cyan-300" />
                      {lang === 'es' ? 'Cumplimiento y textos legales (vigentes)' : 'Legal compliance — in-force texts'}
                    </h3>
                    <p className="text-xs text-pulse-muted mt-1 max-w-2xl">
                      {lang === 'es'
                        ? 'Solo se muestran a usuarios los textos *en vigor* (aprobados). PulseMusic y Alan.inca: estado operativo. El asesor jurídico: documentos completos, versiones e historial en /legal-advisor o Legal Ops.'
                        : 'End users only see in-force (approved) texts. Owner & operations: this operational snapshot. Full editing and history: /legal-advisor (counsel) or Legal Ops.'}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    {compliance ? (
                      <span className="text-2xl font-bold text-cyan-200">
                        {compliance.in_force_count}
                        <span className="text-sm text-pulse-muted font-normal"> / {compliance.in_force_count + (compliance.not_in_force_count || 0)}</span>
                      </span>
                    ) : (
                      <span className="text-xs text-pulse-muted">…</span>
                    )}
                    <p className="text-[10px] text-pulse-muted">
                      {lang === 'es' ? 'Públicos en vigor' : 'public in force'}
                    </p>
                  </div>
                </div>
                {compliance && (compliance.not_in_force_public || []).length > 0 && (
                  <p className="text-xs text-amber-200/90 mt-3">
                    {lang === 'es'
                      ? 'Hay entradas públicas aún no en vigor. El asesor las activa o las retira de visibilidad en Documentos oficiales.'
                      : 'Some public entries are not yet in force. Counsel can activate or hide them in Official documents.'}
                    {' '}
                    <code className="text-[10px] bg-pulse-elevated px-1 rounded">{(compliance.not_in_force_public || []).map((x) => x.slug).join(', ')}</code>
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-4">
                  <Link
                    to="/legal-advisor"
                    className="text-xs font-semibold px-3 py-2 rounded-xl bg-cyan-500/20 text-cyan-100 border border-cyan-500/30 hover:bg-cyan-500/30"
                  >
                    {lang === 'es' ? 'Hub asesor jurídico' : 'Legal counsel hub'}
                  </Link>
                  {isPluka && (
                    <button
                      type="button"
                      onClick={() => { setActiveTab('legal_ops'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      className="text-xs font-semibold px-3 py-2 rounded-xl border border-pulse-border text-pulse-muted hover:text-white"
                    >
                      Legal Ops
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: t('dashboard.artists'), value: analytics.total_artists, color: 'text-pulse-primary' },
                { label: t('dashboard.songs'), value: analytics.total_songs, color: 'text-green-400' },
                { label: t('dashboard.events'), value: analytics.total_events, color: 'text-yellow-400' },
                { label: t('dashboard.users'), value: analytics.total_users, color: 'text-purple-400' },
                { label: t('analytics.total_plays'), value: analytics.total_plays?.toLocaleString(), color: 'text-blue-400' },
                { label: t('analytics.total_likes'), value: analytics.total_likes?.toLocaleString(), color: 'text-red-400' },
                { label: t('dashboard.bookings'), value: analytics.total_bookings, color: 'text-orange-400' },
              ].map((stat, i) => (
                <div key={i} className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                  <p className="text-xs text-pulse-muted">{stat.label}</p>
                  <p className={`text-2xl font-heading font-bold mt-1 ${stat.color}`}>{stat.value || 0}</p>
                </div>
              ))}
            </div>

            <div className="bg-pulse-surface border border-pulse-border rounded-2xl p-5 mb-8">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <h3 className="font-heading font-semibold text-base">{lang === 'es' ? 'Checklist de lanzamiento' : 'Launch checklist'}</h3>
                  <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Te dice exactamente qué falta antes de publicar.' : 'Shows exactly what is still missing before launch.'}</p>
                </div>
                <span className="text-xs px-3 py-1 rounded-full bg-pulse-elevated border border-pulse-border">
                  {launchChecklist.filter(item => item.done).length}/{launchChecklist.length} {lang === 'es' ? 'listo' : 'ready'}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {launchChecklist.map(item => (
                  <div key={item.id} className={`rounded-xl border p-4 ${item.done ? 'border-green-500/30 bg-green-500/10' : 'border-yellow-500/30 bg-yellow-500/10'}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center ${item.done ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-300'}`}>
                        {item.done ? <CheckCircle size={14} /> : <AlertCircle size={14} />}
                      </div>
                      <p className="text-sm font-medium">{item.label}</p>
                    </div>
                    <p className="text-xs text-pulse-muted mt-2">
                      {item.done
                        ? (lang === 'es' ? 'Preparado' : 'Ready')
                        : (lang === 'es' ? 'Pendiente de completar' : 'Still pending')}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="font-heading font-semibold text-sm mb-3">{t('analytics.top_artists')}</h3>
                {analytics.top_artists?.map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-2 text-sm">
                    <span>{a.name}</span>
                    <span className="text-pulse-muted">{a.plays?.toLocaleString()} plays</span>
                  </div>
                ))}
              </div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="font-heading font-semibold text-sm mb-3">{t('analytics.top_songs')}</h3>
                {analytics.top_songs?.slice(0, 5).map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-2 text-sm">
                    <span className="truncate flex-1">{s.title_es}</span>
                    <span className="text-pulse-muted ml-2">{s.plays?.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ══ GOD MODE - Live Control Panel ══ */}
        {activeTab === 'god_mode' && (
          <LiveGodModePanel token={token} isOwner={isOwner} lang={lang} />
        )}

        {/* ══ Financial Analytics ══ */}
        {activeTab === 'financials' && financialData && (
          <div data-testid="dash-financials">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl flex items-center gap-2"><DollarSign size={22} className="text-green-400" /> {lang === 'es' ? 'Panel Financiero' : 'Financial Panel'}</h2>
              <div className="flex gap-1">
                {['7d','30d','90d','365d','all'].map(p => (
                  <button key={p} onClick={() => setFinancialPeriod(p)} className={`px-3 py-1 rounded-lg text-xs ${financialPeriod === p ? 'bg-pulse-primary text-black' : 'bg-pulse-surface border border-pulse-border'}`}>{p === 'all' ? 'Todo' : p}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {[
                { label: lang === 'es' ? 'Depositos totales' : 'Total deposits', val: financialData.total_deposits, color: 'text-green-400', bg: 'border-green-500/20' },
                { label: lang === 'es' ? 'Retiros totales' : 'Total withdrawals', val: financialData.total_withdrawals, color: 'text-orange-400', bg: 'border-orange-500/20' },
                { label: lang === 'es' ? 'Apoyos totales' : 'Total support', val: financialData.total_donations, color: 'text-yellow-400', bg: 'border-yellow-500/20' },
                { label: lang === 'es' ? 'Ingresos bookings' : 'Booking revenue', val: financialData.total_booking_revenue, color: 'text-purple-400', bg: 'border-purple-500/20' },
                { label: lang === 'es' ? 'Ingresos plataforma' : 'Platform revenue', val: financialData.platform_revenue, color: 'text-cyan-400', bg: 'border-cyan-500/20' },
                { label: lang === 'es' ? 'Comision operativa' : 'Operations revenue', val: financialData.pluka_revenue, color: 'text-pink-400', bg: 'border-pink-500/20' },
                { label: lang === 'es' ? 'Margen plataforma' : 'Platform margin', val: financialData.admin_revenue, color: 'text-blue-400', bg: 'border-blue-500/20' },
                { label: lang === 'es' ? 'Saldo en wallets' : 'Wallet balance', val: financialData.total_wallet_balance, color: 'text-white', bg: 'border-white/20' },
              ].map(c => (
                <div key={c.label} className={`bg-pulse-surface border ${c.bg} rounded-xl p-4`}>
                  <p className="text-[10px] text-pulse-muted mb-1">{c.label}</p>
                  <p className={`text-xl font-bold ${c.color}`}>{c.val?.toFixed(2)}€</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <div className="bg-pulse-surface border border-yellow-500/20 rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Escrow congelado' : 'Escrow frozen'}</p><p className="text-xl font-bold text-yellow-400">{financialData.escrow_frozen?.toFixed(2)}€</p></div>
              <div className="bg-pulse-surface border border-green-500/20 rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Escrow liberado' : 'Escrow released'}</p><p className="text-xl font-bold text-green-400">{financialData.escrow_released?.toFixed(2)}€</p></div>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 text-center"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Transacciones' : 'Transactions'}</p><p className="text-2xl font-bold">{financialData.num_transactions}</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 text-center"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Apoyos' : 'Support'}</p><p className="text-2xl font-bold">{financialData.num_donations}</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 text-center"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Bookings pagados' : 'Paid bookings'}</p><p className="text-2xl font-bold">{financialData.num_paid_bookings}</p></div>
            </div>
            {financialData.daily?.length > 0 && (
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="text-sm font-semibold mb-3">{lang === 'es' ? 'Flujo diario' : 'Daily flow'}</h3>
                <div className="h-40 flex items-end gap-1">
                  {financialData.daily.map((d, i) => {
                    const maxVal = Math.max(...financialData.daily.map(x => x.deposits + x.donations)) || 1;
                    const h = ((d.deposits + d.donations) / maxVal) * 100;
                    return <div key={i} className="flex-1 group relative"><div className="bg-pulse-primary/60 rounded-t" style={{ height: `${Math.max(h, 2)}%` }} /><div className="absolute bottom-full mb-1 hidden group-hover:block bg-black border border-pulse-border rounded px-2 py-1 text-[9px] whitespace-nowrap z-10">{d.date}: +{d.deposits?.toFixed(0)}€ dep, +{d.donations?.toFixed(0)}€ don</div></div>;
                  })}
                </div>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button onClick={() => window.open(`${API}/api/admin/export/transactions`, '_blank')} className="text-xs bg-pulse-surface border border-pulse-border px-3 py-2 rounded-lg flex items-center gap-1"><Download size={12} /> {lang === 'es' ? 'Exportar transacciones' : 'Export transactions'}</button>
              <button onClick={() => window.open(`${API}/api/admin/export/donations`, '_blank')} className="text-xs bg-pulse-surface border border-pulse-border px-3 py-2 rounded-lg flex items-center gap-1"><Download size={12} /> {lang === 'es' ? 'Exportar apoyos' : 'Export support'}</button>
            </div>
          </div>
        )}

        {/* ══ User Analytics ══ */}
        {activeTab === 'user_analytics' && userAnalytics && (
          <div data-testid="dash-user-analytics">
            <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2"><Activity size={22} className="text-pulse-primary" /> {lang === 'es' ? 'Inteligencia de Usuarios' : 'User Intelligence'}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">Total</p><p className="text-2xl font-bold">{userAnalytics.total_users}</p></div>
              <div className="bg-pulse-surface border border-green-500/20 rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Verificados' : 'Verified'}</p><p className="text-2xl font-bold text-green-400">{userAnalytics.verified}</p></div>
              <div className="bg-pulse-surface border border-red-500/20 rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Sin verificar' : 'Unverified'}</p><p className="text-2xl font-bold text-red-400">{userAnalytics.unverified}</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Dispositivos' : 'Devices'}</p><div className="flex gap-2 mt-1">{Object.entries(userAnalytics.devices || {}).map(([d, c]) => <span key={d} className="text-xs bg-pulse-elevated px-2 py-0.5 rounded">{d}: {c}</span>)}</div></div>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="text-sm font-semibold mb-3">{lang === 'es' ? 'Por rol' : 'By role'}</h3>
                {Object.entries(userAnalytics.by_role || {}).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between py-1"><span className={`text-xs px-2 py-0.5 rounded-full ${role === 'admin' ? 'bg-purple-500/20 text-purple-400' : role === 'pluka' ? 'bg-pink-500/20 text-pink-400' : role === 'artist' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-gray-500/20 text-gray-400'}`}>{role === 'admin' ? (lang === 'es' ? 'Dirección' : 'Owner') : role === 'pluka' ? (lang === 'es' ? 'Gestión' : 'Operations') : role === 'artist' ? (lang === 'es' ? 'Artista' : 'Artist') : (lang === 'es' ? 'Usuario' : 'User')}</span><span className="font-bold">{count}</span></div>
                ))}
              </div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="text-sm font-semibold mb-3">{lang === 'es' ? 'Top Donantes' : 'Top Donors'}</h3>
                {(userAnalytics.top_donors || []).map((d, i) => (
                  <div key={i} className="flex items-center justify-between py-1 text-xs"><span>{d.name} ({d.count} apoyos)</span><span className="font-bold text-yellow-400">{d.total?.toFixed(2)}€</span></div>
                ))}
                {(userAnalytics.top_donors || []).length === 0 && <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Sin apoyos aun' : 'No support yet'}</p>}
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="text-sm font-semibold mb-3">{lang === 'es' ? 'Top Monederos' : 'Top Wallets'}</h3>
                {(userAnalytics.top_wallets || []).slice(0, 8).map((w, i) => (
                  <div key={i} className="flex items-center justify-between py-1 text-xs"><span>{w.user_info?.name || w.user_info?.email}</span><span className="font-bold text-amber-400">{(w.balance || 0).toLocaleString()} <span className="text-[9px] text-pulse-muted">corcheas</span></span></div>
                ))}
              </div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="text-sm font-semibold mb-3">{lang === 'es' ? 'Top Contratadores' : 'Top Bookers'}</h3>
                {(userAnalytics.top_bookers || []).map((b, i) => (
                  <div key={i} className="flex items-center justify-between py-1 text-xs"><span>{b.user?.name || b.user?.email}</span><span className="font-bold">{b.bookings} bookings</span></div>
                ))}
              </div>
            </div>
            <div className="mt-4"><button onClick={() => window.open(`${API}/api/admin/export/users`, '_blank')} className="text-xs bg-pulse-surface border border-pulse-border px-3 py-2 rounded-lg flex items-center gap-1"><Download size={12} /> {lang === 'es' ? 'Exportar usuarios' : 'Export users'}</button></div>
          </div>
        )}

        {/* ══ Content Analytics ══ */}
        {activeTab === 'content_analytics' && contentAnalytics && (
          <div data-testid="dash-content-analytics">
            <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2"><TrendingUp size={22} className="text-pulse-primary" /> {lang === 'es' ? 'Analisis de Contenido' : 'Content Analytics'}</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Reproducciones' : 'Plays'}</p><p className="text-2xl font-bold text-pulse-primary">{contentAnalytics.total_plays?.toLocaleString()}</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Canciones' : 'Songs'}</p><p className="text-2xl font-bold">{contentAnalytics.total_songs}</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Artistas' : 'Artists'}</p><p className="text-2xl font-bold">{contentAnalytics.total_artists}</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Eventos' : 'Events'}</p><p className="text-2xl font-bold">{contentAnalytics.total_events}</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">Playlists</p><p className="text-2xl font-bold">{contentAnalytics.total_playlists}</p></div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="text-sm font-semibold mb-3">{lang === 'es' ? 'Top Canciones' : 'Top Songs'}</h3>
                {(contentAnalytics.top_songs || []).slice(0, 10).map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-1 text-xs"><span className="truncate max-w-[150px]">{i+1}. {s.title_es}</span><span className="text-pulse-primary font-bold">{s.plays?.toLocaleString()}</span></div>
                ))}
              </div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="text-sm font-semibold mb-3">{lang === 'es' ? 'Top Artistas' : 'Top Artists'}</h3>
                {(contentAnalytics.top_artists || []).slice(0, 10).map((a, i) => (
                  <div key={i} className="flex items-center justify-between py-1 text-xs"><span>{i+1}. {a.name}</span><span className="text-pulse-primary font-bold">{a.plays?.toLocaleString()}</span></div>
                ))}
              </div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-1"><Globe size={14} /> {lang === 'es' ? 'Reproducciones por pais' : 'Plays by country'}</h3>
                {(contentAnalytics.play_locations || []).slice(0, 10).map((l, i) => (
                  <div key={i} className="flex items-center justify-between py-1 text-xs"><span>{l.country}</span><span className="font-bold">{l.plays}</span></div>
                ))}
              </div>
            </div>
            <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 mb-4">
              <h3 className="text-sm font-semibold mb-3">{lang === 'es' ? 'Generos' : 'Genres'}</h3>
              <div className="flex flex-wrap gap-2">
                {(contentAnalytics.genre_distribution || []).map(g => (
                  <span key={g.genre} className="text-xs bg-pulse-elevated border border-pulse-border px-3 py-1.5 rounded-xl">{g.genre} <span className="text-pulse-primary font-bold ml-1">{g.count}</span></span>
                ))}
              </div>
            </div>
            <div className="flex gap-2"><button onClick={() => window.open(`${API}/api/admin/export/songs`, '_blank')} className="text-xs bg-pulse-surface border border-pulse-border px-3 py-2 rounded-lg flex items-center gap-1"><Download size={12} /> {lang === 'es' ? 'Exportar canciones' : 'Export songs'}</button><button onClick={() => window.open(`${API}/api/admin/export/artists`, '_blank')} className="text-xs bg-pulse-surface border border-pulse-border px-3 py-2 rounded-lg flex items-center gap-1"><Download size={12} /> {lang === 'es' ? 'Exportar artistas' : 'Export artists'}</button></div>
          </div>
        )}

        {/* Operations Panel */}
        {activeTab === 'pluka' && (
          <div data-testid="dash-pluka-inbox">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-pulse-primary flex items-center justify-center">
                <span className="text-black font-bold">P</span>
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl">{lang === 'es' ? 'Centro de operaciones' : 'Operations center'}</h2>
                <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Control interno de contrataciones y seguimiento' : 'Internal control for bookings and follow-up'}</p>
              </div>
              {plukaInbox.total_pending > 0 && (
                <span className="ml-auto bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">{plukaInbox.total_pending} {lang === 'es' ? 'pendientes' : 'pending'}</span>
              )}
            </div>

            {/* Pluka Sub-tabs */}
            <div className="flex gap-1 mb-4 flex-wrap bg-pulse-surface rounded-xl p-1">
              {[
                { id: 'inbox', label: lang === 'es' ? 'Bandeja' : 'Inbox' },
                { id: 'ai', label: 'IA Chat' },
                { id: 'contracts', label: lang === 'es' ? 'Contratos' : 'Contracts' },
                { id: 'audio-review', label: lang === 'es' ? 'Revisión audio' : 'Audio review' },
                { id: 'chats', label: lang === 'es' ? 'Todos los chats' : 'All Chats' },
                { id: 'featured', label: lang === 'es' ? 'Selección editorial' : 'Editorial picks' },
                { id: 'calendar', label: lang === 'es' ? 'Calendario' : 'Calendar' },
                { id: 'bulk', label: lang === 'es' ? 'Acciones masivas' : 'Bulk' },
              ].map(st => (
                <button key={st.id} data-testid={`pluka-sub-${st.id}`} onClick={() => setPlukaSubTab(st.id)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${plukaSubTab === st.id ? 'bg-pulse-primary text-black' : 'text-pulse-muted hover:text-white'}`}>{st.label}</button>
              ))}
            </div>

            {/* Sub: Inbox (original) */}
            {plukaSubTab === 'inbox' && (
              <>
                <div className="flex gap-2 mb-4">
                  {['pending', 'confirmed', 'cancelled'].map(tab => (
                    <button key={tab} data-testid={`pluka-tab-${tab}`} onClick={() => { setPlukaTab(tab); setSelectedBooking(null); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${plukaTab === tab ? 'bg-pulse-primary text-black' : 'bg-pulse-surface text-pulse-muted hover:text-white border border-pulse-border'}`}>
                      {tab === 'pending' && <Clock size={14} className="inline mr-1" />}
                      {tab === 'confirmed' && <Check size={14} className="inline mr-1" />}
                      {tab === 'cancelled' && <XCircle size={14} className="inline mr-1" />}
                      {tab === 'pending' ? (lang === 'es' ? 'Pendientes' : 'Pending') : tab === 'confirmed' ? (lang === 'es' ? 'Confirmados' : 'Confirmed') : (lang === 'es' ? 'Cancelados' : 'Cancelled')}
                      {' '}({(plukaInbox[tab] || []).length})
                    </button>
                  ))}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                    {(plukaInbox[plukaTab] || []).map(b => (
                      <button key={b.id} data-testid={`pluka-booking-${b.id}`} onClick={() => openBookingDetail(b)} className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${selectedBooking?.id === b.id ? 'bg-pulse-primary/10 border-pulse-primary/30' : 'bg-pulse-surface border-pulse-border hover:border-cyan-500/20'}`}>
                        <div className="flex items-center gap-3 mb-2">
                          {b.artist_photo && <img src={b.artist_photo} alt="" className="w-10 h-10 rounded-lg object-cover" />}
                          <div className="flex-1 min-w-0"><p className="font-semibold text-sm truncate">{b.artist_name}</p><p className="text-xs text-pulse-muted">{b.user_info?.name || b.user_info?.email || 'Usuario'}</p></div>
                          <span className="text-pulse-primary font-bold text-sm">{b.price}€</span>
                        </div>
                        <div className="flex gap-3 text-xs text-pulse-muted"><span>{b.event_type}</span><span>{b.date}</span><span>{b.location}</span></div>
                      </button>
                    ))}
                    {(plukaInbox[plukaTab] || []).length === 0 && (
                      <div className="text-center py-12 text-pulse-muted text-sm"><Inbox size={32} className="mx-auto mb-2 opacity-40" />{lang === 'es' ? 'No hay contrataciones aqui' : 'No bookings here'}</div>
                    )}
                  </div>
                  {selectedBooking && (
                    <div data-testid="pluka-booking-detail" className="bg-pulse-surface border border-pulse-border rounded-xl p-4 max-h-[70vh] overflow-y-auto">
                      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-pulse-border">
                        {selectedBooking.artist_photo && <img src={selectedBooking.artist_photo} alt="" className="w-14 h-14 rounded-xl object-cover" />}
                        <div><p className="font-heading font-bold">{selectedBooking.artist_name}</p><p className="text-xs text-pulse-muted">{selectedBooking.user_info?.email}</p></div>
                      </div>
                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between"><span className="text-pulse-muted">{lang === 'es' ? 'Tipo' : 'Type'}:</span><span>{selectedBooking.event_type}</span></div>
                        <div className="flex justify-between"><span className="text-pulse-muted">{lang === 'es' ? 'Fecha' : 'Date'}:</span><span>{selectedBooking.date}</span></div>
                        <div className="flex justify-between"><span className="text-pulse-muted">{lang === 'es' ? 'Lugar' : 'Location'}:</span><span>{selectedBooking.location}</span></div>
                        <div className="flex justify-between"><span className="text-pulse-muted">{lang === 'es' ? 'Precio' : 'Price'}:</span><span className="text-pulse-primary font-bold">{selectedBooking.price}€</span></div>
                      </div>
                      <div className="mb-4 rounded-xl border border-pulse-border bg-pulse-elevated p-3 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-xs uppercase tracking-[0.2em] text-pulse-muted">{lang === 'es' ? 'Contrato del booking' : 'Booking contract'}</p>
                            <p className="text-sm font-semibold">{lang === 'es' ? 'Elegir, emitir y firmar como plataforma' : 'Choose, issue and sign as platform'}</p>
                          </div>
                          <Shield size={16} className="text-pulse-primary" />
                        </div>
                        <div className="flex gap-2">
                          <select data-testid="booking-contract-template-select" value={bookingContractTemplateId} onChange={e => setBookingContractTemplateId(e.target.value)} className="flex-1 bg-pulse-surface border border-pulse-border rounded-lg px-3 py-2 text-xs focus:ring-1 focus:ring-cyan-500 focus:outline-none">
                            <option value="">{lang === 'es' ? 'Selecciona plantilla' : 'Select template'}</option>
                            {contractTemplates.filter(item => item.category === 'booking').map(item => <option key={item.id} value={item.id}>{item.title_es || item.title} · v{item.version || 1}</option>)}
                          </select>
                          <button data-testid="booking-contract-assign" onClick={assignBookingContract} className="bg-pulse-primary text-black px-3 py-2 rounded-lg text-xs font-bold">{lang === 'es' ? 'Emitir' : 'Issue'}</button>
                        </div>
                        <div className="space-y-2">
                          {(selectedBooking.contracts || []).map(contract => (
                            <div key={contract.id} className="rounded-lg border border-pulse-border bg-pulse-surface p-3 text-xs">
                              <div className="flex items-center justify-between gap-2">
                                <div>
                                  <p className="font-semibold">{contract.title_es || contract.title_en}</p>
                                  <p className="text-pulse-muted mt-1">{contract.status} · {(contract.required_signers || []).join(', ')}</p>
                                </div>
                                {!(contract.signatures || []).some(sig => sig.signer_role === 'platform') && <button data-testid={`booking-contract-sign-platform-${contract.id}`} onClick={async () => { await fetch(`${API}/api/contracts/instances/${contract.id}/sign`, { method: 'POST', headers, body: JSON.stringify({ signer_name: 'PulseMusic / Pluka' }) }); openBookingDetail(selectedBooking); }} className="border border-pulse-primary/30 text-pulse-primary px-3 py-1.5 rounded-lg">{lang === 'es' ? 'Firmar' : 'Sign'}</button>}
                              </div>
                            </div>
                          ))}
                          {(selectedBooking.contracts || []).length === 0 && <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Aún no hay contrato emitido para esta contratación.' : 'No contract has been issued for this booking yet.'}</p>}
                        </div>
                      </div>
                      {selectedBooking.status === 'pending' && (
                        <div className="flex gap-2 mb-4">
                          <button data-testid="pluka-approve-btn" onClick={() => { updateBookingStatus(selectedBooking.id, 'confirmed'); setSelectedBooking({ ...selectedBooking, status: 'confirmed' }); }} className="flex-1 bg-green-500/20 text-green-400 py-2 rounded-lg text-sm font-medium hover:bg-green-500/30 flex items-center justify-center gap-1"><Check size={14} /> {lang === 'es' ? 'Aprobar' : 'Approve'}</button>
                          <button data-testid="pluka-reject-btn" onClick={() => { updateBookingStatus(selectedBooking.id, 'cancelled'); setSelectedBooking({ ...selectedBooking, status: 'cancelled' }); }} className="flex-1 bg-red-500/20 text-red-400 py-2 rounded-lg text-sm font-medium hover:bg-red-500/30 flex items-center justify-center gap-1"><XCircle size={14} /> {lang === 'es' ? 'Rechazar' : 'Reject'}</button>
                        </div>
                      )}
                      <div className="mb-3">
                        <h4 className="text-xs text-pulse-muted mb-2 flex items-center gap-1"><MessageSquare size={12} /> Chat</h4>
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {bookingMessages.map(msg => (
                            <div key={msg.id} className={`p-2 rounded-lg text-xs ${msg.sender === 'pluka' || msg.sender === 'admin' ? 'bg-pulse-primary/10 ml-8' : msg.sender === 'ai' ? 'bg-purple-500/10 ml-8' : 'bg-pulse-elevated mr-8'}`}>
                              <span className="font-bold text-[10px]">{msg.sender_name || msg.sender}{msg.is_ai ? ' (IA)' : msg.is_manual ? ' (manual)' : ''}</span>
                              <p className="mt-0.5">{msg.message}</p>
                              {msg.sender !== 'pluka' && msg.sender !== 'admin' && <button data-testid={`translate-booking-msg-${msg.id}`} onClick={() => translateOpsText(msg.message, msg.id)} className="mt-2 text-[10px] text-pulse-primary inline-flex items-center gap-1"><Languages size={10} /> {lang === 'es' ? 'Traducir' : 'Translate'}</button>}
                              {messageTranslations[msg.id] && <p data-testid={`translated-booking-msg-${msg.id}`} className="mt-2 text-[11px] text-cyan-200">{messageTranslations[msg.id]}</p>}
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2 mt-2">
                          <input data-testid="pluka-reply-input" value={replyMsg} onChange={e => setReplyMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendBookingReply()} placeholder={lang === 'es' ? 'Responder manualmente...' : 'Reply manually...'} className="flex-1 bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-xs focus:ring-1 focus:ring-cyan-500 focus:outline-none" />
                          <button data-testid="pluka-reply-translate-en" onClick={translateReplyToEnglish} className="border border-pulse-border px-3 py-2 rounded-lg text-xs text-pulse-muted hover:text-white"><Languages size={12} /></button>
                          <button data-testid="pluka-send-reply" onClick={sendBookingReply} disabled={sendingMsg} className="bg-pulse-primary text-black px-3 py-2 rounded-lg text-xs font-bold disabled:opacity-50"><Send size={12} /></button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Sub: AI Chat */}
            {plukaSubTab === 'ai' && (
              <div className="bg-pulse-surface border border-pulse-border rounded-xl overflow-hidden" style={{height: '60vh'}}>
                <div className="p-3 border-b border-pulse-border bg-gradient-to-r from-purple-500/10 to-cyan-500/10">
                  <p className="text-sm font-semibold">{lang === 'es' ? 'Asistente IA de operaciones' : 'Operations AI assistant'}</p>
                  <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Preguntame cualquier cosa sobre la plataforma' : 'Ask me anything about the platform'}</p>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{height: 'calc(60vh - 110px)'}}>
                  {plukaAiChat.length === 0 && (
                    <div className="text-center py-8">
                      <p className="text-pulse-muted text-sm">{lang === 'es' ? 'Hola. Puedo analizar la plataforma y ejecutar acciones reales con confirmacion segura.' : 'Hi. I can analyze the platform and execute real actions with secure confirmation.'}</p>
                      <div className="flex flex-wrap gap-2 justify-center mt-3">
                        {[
                          lang === 'es' ? 'Que artistas estan disponibles en Madrid el 2026-04-10?' : 'Available artists in Madrid on 2026-04-10?',
                          lang === 'es' ? 'Añade un evento llamado Valencia Beats Pro el 2026-05-12 en Valencia en Sala Pulse, genero Urban, precio 35, aforo 600' : 'Create an event in Valencia on 2026-05-12',
                          lang === 'es' ? 'Resumen financiero y bookings pendientes' : 'Financial summary and pending bookings',
                          lang === 'es' ? 'Pon a El Mago como no disponible' : 'Set El Mago as unavailable'
                        ].map(q => (
                          <button key={q} onClick={() => { setPlukaAiMsg(q); }} className="text-xs bg-pulse-elevated border border-pulse-border px-3 py-1.5 rounded-full hover:border-cyan-500/30 transition-colors">{q}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {plukaAiChat.map((msg, i) => (
                    <div key={i} className={`max-w-[80%] ${msg.role === 'user' ? 'ml-auto' : ''}`}>
                      <div className={`p-3 rounded-xl text-sm ${msg.role === 'user' ? 'bg-pulse-primary/20 text-white' : 'bg-pulse-elevated text-white'}`}>
                        {msg.content}
                      </div>
                    </div>
                  ))}
                  {plukaAiLoading && (
                    <div className="flex items-center gap-2 text-pulse-muted text-xs"><div className="w-4 h-4 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" /> {lang === 'es' ? 'Pensando...' : 'Thinking...'}</div>
                  )}
                  {plukaAiPendingAction && (
                    <div data-testid="pluka-ai-pending-action" className="border border-yellow-500/30 bg-yellow-500/10 rounded-xl p-4 space-y-3">
                      <div>
                        <p className="text-sm font-semibold text-yellow-300">{lang === 'es' ? 'Accion lista para ejecutar' : 'Action ready to execute'}</p>
                        <p className="text-xs text-pulse-muted mt-1">{plukaAiPendingAction.summary}</p>
                      </div>
                      <input
                        data-testid="pluka-ai-confirm-password"
                        type="password"
                        value={plukaAiPassword}
                        onChange={(e) => setPlukaAiPassword(e.target.value)}
                        placeholder={lang === 'es' ? 'Escribe tu clave para confirmar' : 'Enter your password to confirm'}
                        className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-1 focus:ring-yellow-400 focus:outline-none"
                      />
                      <div className="flex flex-wrap gap-2">
                        <button
                          data-testid="pluka-ai-confirm-action"
                          onClick={confirmPlukaAiAction}
                          disabled={plukaAiActionLoading || !plukaAiPassword.trim()}
                          className="bg-yellow-400 text-black px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50"
                        >
                          {plukaAiActionLoading ? '...' : (lang === 'es' ? 'Confirmar y ejecutar' : 'Confirm and execute')}
                        </button>
                        <button
                          data-testid="pluka-ai-dismiss-action"
                          onClick={() => { setPlukaAiPendingAction(null); setPlukaAiPassword(''); }}
                          className="bg-pulse-elevated border border-pulse-border px-4 py-2 rounded-lg text-sm"
                        >
                          {lang === 'es' ? 'Descartar' : 'Dismiss'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="p-3 border-t border-pulse-border flex gap-2">
                  <input data-testid="pluka-ai-input" value={plukaAiMsg} onChange={e => setPlukaAiMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendPlukaAi()} placeholder={lang === 'es' ? 'Pregunta a la IA...' : 'Ask AI...'} className="flex-1 bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-1 focus:ring-cyan-500 focus:outline-none" />
                  <button data-testid="pluka-ai-send" onClick={sendPlukaAi} disabled={plukaAiLoading || !plukaAiMsg.trim()} className="bg-pulse-primary text-black px-4 py-2.5 rounded-lg text-sm font-bold disabled:opacity-50"><Send size={14} /></button>
                </div>
              </div>
            )}

            {plukaSubTab === 'contracts' && (
              <ContractsManagerPanel api={API} headers={headers} lang={lang} />
            )}

            {plukaSubTab === 'audio-review' && (
              <AudioReviewPanel
                items={audioReviewQueue.songs || []}
                stats={audioReviewQueue.stats || {}}
                lang={lang}
                onDecision={updateSongVerificationDecision}
                onRerun={rerunSongVerification}
                busySongId={audioReviewBusy}
              />
            )}

            {/* Sub: All Chats (God view) */}
            {plukaSubTab === 'chats' && (
              <div className="space-y-3">
                <p className="text-xs text-pulse-muted mb-2">{lang === 'es' ? `${plukaAllChats.length} conversaciones de contrataciones` : `${plukaAllChats.length} booking conversations`}</p>
                {plukaAllChats.map(chat => (
                  <div key={chat.id} className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {chat.artist_photo && <img src={chat.artist_photo} alt="" className="w-8 h-8 rounded-lg object-cover" />}
                        <div><p className="text-sm font-semibold">{chat.artist_name}</p><p className="text-[10px] text-pulse-muted">{chat.user_info?.name || chat.user_info?.email || '-'} | {chat.status}</p></div>
                      </div>
                      <span className="text-xs text-pulse-primary font-bold">{chat.price}€</span>
                    </div>
                    {chat.messages?.length > 0 && (
                      <div className="space-y-1.5 max-h-40 overflow-y-auto mb-2">
                        {chat.messages.slice(-5).map(m => (
                          <div key={m.id} className={`text-xs p-2 rounded-lg ${m.sender === 'pluka' || m.sender === 'admin' ? 'bg-cyan-500/10 ml-6' : m.sender === 'ai' ? 'bg-purple-500/10 ml-6' : 'bg-pulse-elevated mr-6'}`}>
                            <span className="font-bold text-[10px]">{m.sender_name || m.sender}{m.is_ai ? ' (IA)' : m.is_manual ? ' (manual)' : ''}</span>
                            <p className="mt-0.5">{m.message}</p>
                            {m.sender !== 'pluka' && m.sender !== 'admin' && <button data-testid={`translate-allchat-msg-${m.id}`} onClick={() => translateOpsText(m.message, `all-${m.id}`)} className="mt-2 text-[10px] text-pulse-primary inline-flex items-center gap-1"><Languages size={10} /> {lang === 'es' ? 'Traducir' : 'Translate'}</button>}
                            {messageTranslations[`all-${m.id}`] && <p className="mt-2 text-[11px] text-cyan-200">{messageTranslations[`all-${m.id}`]}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input placeholder={lang === 'es' ? 'Responder desde gestión...' : 'Reply from operations...'} onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { plukaReply(chat.id, e.target.value); e.target.value = ''; } }} className="flex-1 bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-1.5 text-xs focus:ring-1 focus:ring-cyan-500 focus:outline-none" />
                    </div>
                  </div>
                ))}
                {plukaAllChats.length === 0 && <p className="text-center text-pulse-muted py-8 text-sm">{lang === 'es' ? 'Sin conversaciones' : 'No conversations'}</p>}
              </div>
            )}

            {/* Sub: Featured Artists */}
            {plukaSubTab === 'featured' && (
              <div>
                <p className="text-sm mb-4">{lang === 'es' ? 'Selecciona los artistas que quieres destacar en la Home. Si no seleccionas ninguno, se muestran los mas populares automaticamente.' : 'Select artists to feature on Home. If none selected, most popular are shown automatically.'}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {plukaFeatured.all_artists?.map(a => {
                    const isFeat = (plukaFeatured.featured_ids || []).includes(a.id);
                    return (
                      <button key={a.id} onClick={() => toggleFeaturedArtist(a.id)} className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${isFeat ? 'border-pulse-primary bg-pulse-primary/10' : 'border-pulse-border bg-pulse-surface hover:border-cyan-500/20'}`}>
                        <img src={a.photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                        <div className="text-left min-w-0">
                          <p className="text-sm font-medium truncate">{a.name}</p>
                          <p className="text-[10px] text-pulse-muted">{a.plays ? `${(a.plays/1000).toFixed(0)}K plays` : ''}</p>
                        </div>
                        {isFeat && <Star size={14} className="text-yellow-400 ml-auto" fill="currentColor" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sub: Calendar */}
            {plukaSubTab === 'calendar' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-sm">{lang === 'es' ? 'Proximas contrataciones y eventos' : 'Upcoming bookings and events'}</h3>
                {[...(plukaCalendar.bookings || []).map(b => ({ ...b, _type: 'booking' })), ...(plukaCalendar.events || []).map(e => ({ ...e, _type: 'event' }))].sort((a, b) => (a.date || '').localeCompare(b.date || '')).map((item, i) => (
                  <div key={i} className={`flex items-center gap-4 p-4 rounded-xl border ${item._type === 'booking' ? 'border-cyan-500/20 bg-cyan-500/5' : 'border-purple-500/20 bg-purple-500/5'}`}>
                    <div className="w-12 h-12 rounded-xl bg-pulse-elevated flex flex-col items-center justify-center">
                      <span className="text-[10px] text-pulse-muted">{item.date ? new Date(item.date).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { month: 'short' }).toUpperCase() : '-'}</span>
                      <span className="text-lg font-bold">{item.date ? new Date(item.date).getDate() : '-'}</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{item._type === 'booking' ? item.artist_name : (item.title_es || item.title_en)}</p>
                      <p className="text-xs text-pulse-muted">{formatCalendarLocation(item.location)} | {item._type === 'booking' ? `${item.price || 0}€` : `${item.price || 0}€`}</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${item.status === 'confirmed' ? 'bg-green-500/20 text-green-400' : item.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-pulse-elevated text-pulse-muted'}`}>{item.status || item._type}</span>
                  </div>
                ))}
                {(plukaCalendar.bookings || []).length === 0 && (plukaCalendar.events || []).length === 0 && (
                  <p className="text-center text-pulse-muted py-8">{lang === 'es' ? 'Sin eventos proximos' : 'No upcoming events'}</p>
                )}
              </div>
            )}

            {/* Sub: Bulk Actions */}
            {plukaSubTab === 'bulk' && (
              <div>
                <p className="text-sm mb-4">{lang === 'es' ? 'Selecciona contrataciones pendientes y aplica acciones masivas' : 'Select pending bookings for bulk actions'}</p>
                <div className="flex gap-2 mb-4">
                  <button onClick={() => bulkAction('confirm')} disabled={bulkSelected.length === 0} className="bg-green-500/20 text-green-400 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-30 flex items-center gap-1"><Check size={14} /> {lang === 'es' ? `Aprobar (${bulkSelected.length})` : `Approve (${bulkSelected.length})`}</button>
                  <button onClick={() => bulkAction('cancel')} disabled={bulkSelected.length === 0} className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-30 flex items-center gap-1"><XCircle size={14} /> {lang === 'es' ? `Rechazar (${bulkSelected.length})` : `Reject (${bulkSelected.length})`}</button>
                  <button onClick={() => setBulkSelected(plukaInbox.pending?.map(b => b.id) || [])} className="text-xs text-pulse-muted hover:text-white px-2">{lang === 'es' ? 'Seleccionar todos' : 'Select all'}</button>
                </div>
                <div className="space-y-2">
                  {(plukaInbox.pending || []).map(b => (
                    <label key={b.id} className="flex items-center gap-3 p-3 rounded-xl bg-pulse-surface border border-pulse-border cursor-pointer hover:border-cyan-500/20">
                      <input type="checkbox" checked={bulkSelected.includes(b.id)} onChange={e => setBulkSelected(prev => e.target.checked ? [...prev, b.id] : prev.filter(id => id !== b.id))} className="accent-pulse-primary" />
                      {b.artist_photo && <img src={b.artist_photo} alt="" className="w-8 h-8 rounded-lg object-cover" />}
                      <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{b.artist_name}</p><p className="text-xs text-pulse-muted">{b.event_type} | {b.date} | {b.location}</p></div>
                      <span className="text-pulse-primary font-bold text-sm">{b.price}€</span>
                    </label>
                  ))}
                  {(plukaInbox.pending || []).length === 0 && <p className="text-center text-pulse-muted py-8 text-sm">{lang === 'es' ? 'No hay contrataciones pendientes' : 'No pending bookings'}</p>}
                </div>
              </div>
            )}

            {/* Escrow Management (inside Pluka inbox) */}
            {plukaSubTab === 'inbox' && (
            <div className="mt-8">
              <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><CreditCard size={18} className="text-yellow-400" /> {lang === 'es' ? 'Pagos en Escrow (70% artista congelado)' : 'Escrow Payments'}</h3>
              {!isOwner && <p data-testid="pluka-finance-readonly" className="text-xs text-pulse-muted mb-3">{lang === 'es' ? 'Pluka puede supervisar este flujo, pero solo admin libera fondos y mueve el reparto fijo.' : 'Pluka can supervise this flow, but only admin can release funds and move the fixed split.'}</p>}
              <div className="space-y-2">
                {(plukaInbox.confirmed || []).filter(b => b.paid && !b.escrow_released).map(b => (
                  <div key={b.id} className="flex items-center gap-3 p-4 bg-pulse-surface border border-yellow-500/20 rounded-xl">
                    <div className="flex-1">
                      <p className="font-semibold text-sm">{b.artist_name} - {b.event_type}</p>
                      <p className="text-xs text-pulse-muted">{b.date} | {b.location} | Total: {b.total_price}€</p>
                      <div className="flex gap-4 text-xs mt-1">
                        <span className="text-green-400">Artista: {b.artist_share}€</span>
                        <span className="text-purple-400">{lang === 'es' ? 'Gestión' : 'Operations'}: {b.pluka_share}€</span>
                        <span className="text-blue-400">{lang === 'es' ? 'Plataforma' : 'Platform'}: {b.admin_share}€</span>
                      </div>
                    </div>
                    {isOwner && <button data-testid={`release-${b.id}`} onClick={async () => {
                      if (!window.confirm(lang === 'es' ? 'Confirmas que el evento se realizo y liberas los fondos?' : 'Confirm event happened and release funds?')) return;
                      await fetch(`${API}/api/pluka/release/${b.id}`, { method: 'POST', headers });
                      fetchAll();
                    }} className="bg-green-500/20 text-green-400 px-4 py-2 rounded-xl text-sm font-medium hover:bg-green-500/30 transition-colors">{lang === 'es' ? 'Liberar Fondos' : 'Release Funds'}</button>}
                  </div>
                ))}
                {(plukaInbox.confirmed || []).filter(b => b.paid && !b.escrow_released).length === 0 && (
                  <p className="text-pulse-muted text-sm text-center py-4">{lang === 'es' ? 'No hay fondos congelados' : 'No frozen funds'}</p>
                )}
              </div>
            </div>
            )}
          </div>
        )}

        {/* Artists Management */}
        {activeTab === 'artists' && (
          <div data-testid="dash-artists">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl">{t('dashboard.artists')}</h2>
              <button
                data-testid="add-artist-btn"
                onClick={() => { setEditingArtist('new'); setArtistForm({ name: '', genre: '', bio_es: '', bio_en: '', photo: '', cover: '', price_per_show: 0, city: '' }); }}
                className="flex items-center gap-2 bg-pulse-primary text-black px-4 py-2 rounded-xl text-sm font-semibold"
              >
                <Plus size={16} /> {t('common.create')}
              </button>
            </div>

            {(editingArtist) && (
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 mb-6 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Nombre" value={artistForm.name} onChange={e => setArtistForm(f => ({...f, name: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Generos (comma sep)" value={artistForm.genre} onChange={e => setArtistForm(f => ({...f, genre: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Bio (ES)" value={artistForm.bio_es} onChange={e => setArtistForm(f => ({...f, bio_es: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Bio (EN)" value={artistForm.bio_en} onChange={e => setArtistForm(f => ({...f, bio_en: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Photo URL" value={artistForm.photo} onChange={e => setArtistForm(f => ({...f, photo: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Cover URL" value={artistForm.cover} onChange={e => setArtistForm(f => ({...f, cover: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input type="number" placeholder="Precio/Show" value={artistForm.price_per_show} onChange={e => setArtistForm(f => ({...f, price_per_show: parseFloat(e.target.value) || 0}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Ciudad" value={artistForm.city} onChange={e => setArtistForm(f => ({...f, city: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                </div>
                <div className="flex gap-2">
                  <button onClick={saveArtist} className="bg-pulse-primary text-black px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1"><Save size={14} /> {t('common.save')}</button>
                  <button onClick={() => setEditingArtist(null)} className="text-pulse-muted px-4 py-2 text-sm">{t('common.cancel')}</button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {artists.map(a => (
                <div key={a.id} className="flex items-center gap-4 p-3 bg-pulse-surface border border-pulse-border rounded-xl">
                  <img src={a.photo} alt={a.name} className="w-12 h-12 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{a.name}</p>
                    <p className="text-xs text-pulse-muted">{a.city} - {Array.isArray(a.genre) ? a.genre.join(', ') : (a.genre || '')}</p>
                  </div>
                  <span className="text-sm text-pulse-primary font-semibold">{a.price_per_show}€</span>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditingArtist(a.id); setArtistForm({ ...a, genre: Array.isArray(a.genre) ? a.genre.join(', ') : (a.genre || '') }); }} className="p-2 hover:bg-pulse-elevated rounded-lg"><Edit size={14} /></button>
                    <button onClick={() => deleteArtist(a.id)} className="p-2 hover:bg-red-500/10 rounded-lg text-red-400"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Songs Management */}
        {activeTab === 'songs' && (
          <div data-testid="dash-songs">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl">{t('dashboard.songs')}</h2>
              <button onClick={() => setShowSongForm(!showSongForm)} className="flex items-center gap-2 bg-pulse-primary text-black px-4 py-2 rounded-xl text-sm font-semibold">
                <Plus size={16} /> {t('common.create')}
              </button>
            </div>

            {showSongForm && (
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 mb-6 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Titulo (ES)" value={songForm.title_es} onChange={e => setSongForm(f => ({...f, title_es: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Titulo (EN)" value={songForm.title_en} onChange={e => setSongForm(f => ({...f, title_en: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <select value={songForm.artist_id} onChange={e => { const a = artists.find(x => x.id === e.target.value); setSongForm(f => ({...f, artist_id: e.target.value, artist_name: a?.name || ''})); }} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none">
                    <option value="">Seleccionar artista</option>
                    {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                  <input placeholder="Audio URL" value={songForm.audio_url} onChange={e => setSongForm(f => ({...f, audio_url: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Cover URL" value={songForm.cover} onChange={e => setSongForm(f => ({...f, cover: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input type="number" placeholder="Duration (sec)" value={songForm.duration} onChange={e => setSongForm(f => ({...f, duration: parseInt(e.target.value) || 0}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Genero" value={songForm.genre} onChange={e => setSongForm(f => ({...f, genre: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="ISRC (opcional)" value={songForm.isrc || ''} onChange={e => setSongForm(f => ({...f, isrc: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                </div>
                <div className="flex gap-2">
                  <button onClick={saveSong} className="bg-pulse-primary text-black px-4 py-2 rounded-lg text-sm font-semibold"><Save size={14} className="inline mr-1" />{t('common.save')}</button>
                  <button onClick={() => setShowSongForm(false)} className="text-pulse-muted px-4 py-2 text-sm">{t('common.cancel')}</button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {songs.map(s => (
                <div key={s.id} className="flex items-center gap-4 p-3 bg-pulse-surface border border-pulse-border rounded-xl">
                  <img src={s.cover} alt={s.title_es} className="w-10 h-10 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{s.title_es}</p>
                    <p className="text-xs text-pulse-muted">{s.artist_name}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${s.status === 'approved' ? 'bg-green-500/15 text-green-300' : s.status === 'rejected' ? 'bg-red-500/15 text-red-300' : s.status === 'pending_review' ? 'bg-cyan-500/15 text-cyan-200' : 'bg-amber-500/15 text-amber-300'}`}>{s.status || 'approved'}</span>
                      <span className="text-[10px] text-pulse-muted">Trust {s.trust_score ?? 0}</span>
                      {s.isrc && <span className="text-[10px] text-pulse-muted">ISRC {s.isrc}</span>}
                    </div>
                  </div>
                  <span className="text-xs text-pulse-muted">{s.plays} plays</span>
                  <button data-testid={`song-rerun-${s.id}`} onClick={() => rerunSongVerification(s.id)} className="p-2 rounded-lg border border-pulse-border text-pulse-muted hover:text-white hover:border-cyan-500/30">
                    <RefreshCw size={14} />
                  </button>
                  <button onClick={() => deleteSong(s.id)} className="p-2 hover:bg-red-500/10 rounded-lg text-red-400"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Events Management */}
        {activeTab === 'events' && (
          <div data-testid="dash-events">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-bold text-2xl">{t('dashboard.events')}</h2>
              <button onClick={() => setShowEventForm(!showEventForm)} className="flex items-center gap-2 bg-pulse-primary text-black px-4 py-2 rounded-xl text-sm font-semibold">
                <Plus size={16} /> {t('common.create')}
              </button>
            </div>

            {showEventForm && (
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 mb-6 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Titulo (ES)" value={eventForm.title_es} onChange={e => setEventForm(f => ({...f, title_es: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Titulo (EN)" value={eventForm.title_en} onChange={e => setEventForm(f => ({...f, title_en: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input type="date" value={eventForm.date} onChange={e => setEventForm(f => ({...f, date: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Genero" value={eventForm.genre} onChange={e => setEventForm(f => ({...f, genre: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Ciudad" value={eventForm.location.name} onChange={e => setEventForm(f => ({...f, location: {...f.location, name: e.target.value}}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Venue" value={eventForm.location.venue} onChange={e => setEventForm(f => ({...f, location: {...f.location, venue: e.target.value}}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input type="number" placeholder="Precio" value={eventForm.price} onChange={e => setEventForm(f => ({...f, price: parseFloat(e.target.value) || 0}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <input placeholder="Image URL" value={eventForm.image} onChange={e => setEventForm(f => ({...f, image: e.target.value}))} className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                </div>
                <div className="flex gap-2">
                  <button onClick={saveEvent} className="bg-pulse-primary text-black px-4 py-2 rounded-lg text-sm font-semibold"><Save size={14} className="inline mr-1" />{t('common.save')}</button>
                  <button onClick={() => setShowEventForm(false)} className="text-pulse-muted px-4 py-2 text-sm">{t('common.cancel')}</button>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {events.map(e => (
                <div key={e.id} className="flex items-center gap-4 p-3 bg-pulse-surface border border-pulse-border rounded-xl">
                  <img src={e.image} alt={e.title_es} className="w-12 h-12 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{e.title_es}</p>
                    <p className="text-xs text-pulse-muted">{e.location?.name} - {new Date(e.date).toLocaleDateString()}</p>
                  </div>
                  <button onClick={() => deleteEvent(e.id)} className="p-2 hover:bg-red-500/10 rounded-lg text-red-400"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users */}
        {activeTab === 'users' && (
          <div data-testid="dash-users">
            <h2 className="font-heading font-bold text-2xl mb-6">{t('dashboard.users')}</h2>

            {/* Invite */}
            <form onSubmit={inviteUser} className="bg-pulse-surface border border-pulse-border rounded-xl p-4 mb-6">
              <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2"><Mail size={14} /> {t('dashboard.invite')}</h3>
              <div className="flex gap-3 flex-wrap">
                <input
                  data-testid="invite-email"
                  type="email"
                  value={inviteForm.email}
                  onChange={e => setInviteForm(f => ({...f, email: e.target.value}))}
                  placeholder="Email"
                  className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm flex-1 min-w-[200px] focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                  required
                />
                <input
                  data-testid="invite-name"
                  type="text"
                  value={inviteForm.name}
                  onChange={e => setInviteForm(f => ({...f, name: e.target.value}))}
                  placeholder="Nombre"
                  className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm flex-1 min-w-[150px] focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                />
                <select
                  value={inviteForm.role}
                  onChange={e => setInviteForm(f => ({...f, role: e.target.value}))}
                  className="bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                >
                  <option value="user">User</option>
                  <option value="artist">Artist</option>
                  <option value="moderator">{lang === 'es' ? 'Moderación' : 'Moderator'}</option>
                  <option value="legal_advisor">{lang === 'es' ? 'Asesor legal' : 'Legal advisor'}</option>
                  <option value="pluka">{lang === 'es' ? 'Gestión' : 'Operations'}</option>
                  <option value="admin">{lang === 'es' ? 'Dirección' : 'Owner'}</option>
                </select>
                <button type="submit" data-testid="invite-submit" className="bg-pulse-primary text-black px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1">
                  <Send size={14} /> {t('dashboard.invite')}
                </button>
              </div>
              {inviteResult && (
                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg text-sm">
                  <p className="text-green-400 flex items-center gap-1"><CheckCircle size={14} /> {inviteResult.message}</p>
                  <p className="text-xs text-pulse-muted mt-1">{inviteResult.email_sent ? (lang === 'es' ? 'La persona recibirá un enlace seguro para verificar el acceso y crear contraseña.' : 'The person will receive a secure link to verify access and create a password.') : (lang === 'es' ? 'Falta configurar SendGrid para poder enviar el enlace.' : 'SendGrid must be configured before the link can be delivered.')}</p>
                </div>
              )}
            </form>

            {(isOwner || isPluka) && modRequests.filter(r => r.status === 'pending').length > 0 && (
              <div className="bg-amber-500/10 border border-amber-500/25 rounded-xl p-4 mb-6">
                <h3 className="font-heading font-semibold text-sm mb-3 text-amber-200">
                  {lang === 'es' ? 'Cola: moderación (archivo legal)' : 'Queue: moderation (legal file)'}
                </h3>
                <div className="space-y-3">
                  {modRequests.filter(r => r.status === 'pending').map((r) => (
                    <div key={r.user_id} className="flex flex-wrap items-center gap-2 justify-between bg-pulse-elevated/50 rounded-lg px-3 py-2 text-xs">
                      <div>
                        <span className="text-white font-medium">{r.full_name || r.display_name}</span>
                        <span className="text-pulse-muted ml-2">{r.email}</span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={async () => {
                            await fetch(`${API}/api/admin/moderator-requests/${r.user_id}/approve`, { method: 'POST', headers });
                            fetchModRequests();
                            fetchAll();
                          }}
                          className="px-3 py-1 rounded-lg bg-green-500/20 text-green-400 hover:bg-green-500/30"
                        >
                          {lang === 'es' ? 'Aprobar' : 'Approve'}
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            const reason = window.prompt(lang === 'es' ? 'Motivo del rechazo (opcional)' : 'Rejection reason (optional)') || '';
                            await fetch(`${API}/api/admin/moderator-requests/${r.user_id}/reject`, {
                              method: 'POST',
                              headers,
                              body: JSON.stringify({ reason }),
                            });
                            fetchModRequests();
                            fetchAll();
                          }}
                          className="px-3 py-1 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                        >
                          {lang === 'es' ? 'Denegar' : 'Reject'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              {users.map(u => (
                <div key={u.id} className="flex items-center gap-4 p-3 bg-pulse-surface border border-pulse-border rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-pulse-elevated flex items-center justify-center">
                    <Shield size={14} className={u.role === 'admin' ? 'text-pulse-primary' : 'text-pulse-muted'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm">{u.name}</p>
                    <p className="text-xs text-pulse-muted">{u.email}</p>
                    {u.must_set_password && <p className="text-[10px] text-yellow-400 mt-1">{lang === 'es' ? 'Acceso pendiente de activación' : 'Access pending activation'}</p>}
                  </div>
                  <select
                    disabled={!isOwner && u.role === 'admin'}
                    value={u.role}
                    onChange={async (e) => {
                      await fetch(`${API}/api/admin/users/${u.id}/role`, { method: 'PUT', headers, body: JSON.stringify({ role: e.target.value }) });
                      fetchAll();
                    }}
                    className="bg-pulse-elevated border border-pulse-border rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-cyan-500 focus:outline-none disabled:opacity-40"
                  >
                    <option value="user">{lang === 'es' ? 'Usuario' : 'User'}</option>
                    <option value="artist">{lang === 'es' ? 'Artista' : 'Artist'}</option>
                    <option value="moderator">{lang === 'es' ? 'Moderación' : 'Moderator'}</option>
                    <option value="pluka">{lang === 'es' ? 'Gestión' : 'Operations'}</option>
                    {isOwner && <option value="admin">{lang === 'es' ? 'Dirección' : 'Owner'}</option>}
                  </select>
                  <button disabled={!isOwner && u.role === 'admin'} onClick={() => deleteUser(u.id)} className="p-2 hover:bg-red-500/10 rounded-lg text-red-400 disabled:opacity-40"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bookings */}
        {activeTab === 'bookings' && (
          <div data-testid="dash-bookings">
            <h2 className="font-heading font-bold text-2xl mb-6">{t('dashboard.bookings')}</h2>
            {inquireOffers.length > 0 && (
              <div className="mb-6 rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-4">
                <h3 className="text-sm font-heading font-bold text-cyan-200 mb-2">
                  {lang === 'es' ? 'Consultas (precontrato) V2' : 'Inquiry offers (V2)'}
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {inquireOffers.map((o) => (
                    <div key={o.id} className="flex items-center justify-between text-xs p-2 rounded-lg bg-pulse-surface border border-pulse-border">
                      <span className="text-pulse-muted shrink min-w-0">
                        {o.artist_name} — {o.status} — {o.created_at?.slice(0, 10)}
                      </span>
                      <Link to={`/inquire/${o.id}`} className="text-pulse-primary font-semibold shrink-0">
                        {lang === 'es' ? 'Ver' : 'View'}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="space-y-2">
              {bookings.map(b => (
                <div key={b.id} className="p-4 bg-pulse-surface border border-pulse-border rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-sm">{b.artist_name}</p>
                      <p className="text-xs text-pulse-muted">{b.event_type} - {b.date} - {b.location}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      b.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                      b.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>{b.status}</span>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => updateBookingStatus(b.id, 'confirmed')} className="text-xs bg-green-500/10 text-green-400 px-3 py-1 rounded-lg">Confirmar</button>
                    <button onClick={() => updateBookingStatus(b.id, 'cancelled')} className="text-xs bg-red-500/10 text-red-400 px-3 py-1 rounded-lg">Cancelar</button>
                    <Link to={`/booking/${b.artist_id}`} className="text-xs text-pulse-primary ml-auto">Ver chat</Link>
                  </div>
                </div>
              ))}
              {bookings.length === 0 && <p className="text-pulse-muted text-sm">{lang === 'es' ? 'Sin contrataciones' : 'No bookings'}</p>}
            </div>
          </div>
        )}

        {/* Wallets Admin */}
        {activeTab === 'wallets' && (
          <div data-testid="dash-wallets">
            <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2"><Wallet size={22} className="text-pulse-primary" /> {lang === 'es' ? 'Monederos' : 'Wallets'}</h2>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Total Monederos' : 'Total Wallets'}</p><p className="text-2xl font-bold text-pulse-primary">{adminWallets.length}</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Saldo Total' : 'Total Balance'}</p><p className="text-2xl font-bold text-amber-400">{adminWallets.reduce((s, w) => s + (w.balance || 0), 0).toLocaleString()} <span className="text-sm text-pulse-muted">corcheas</span></p><p className="text-xs text-pulse-muted">{(adminWallets.reduce((s, w) => s + (w.balance_fiat || 0), 0)).toFixed(2)}EUR</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Comision' : 'Commission'}</p><p className="text-2xl font-bold text-yellow-400">{((settings.commission_rate || 0.30) * 100).toFixed(0)}%</p></div>
            </div>
            <div className="bg-pulse-surface border border-pulse-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-pulse-border text-xs text-pulse-muted"><th className="p-3 text-left">{lang === 'es' ? 'Usuario' : 'User'}</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Rol</th><th className="p-3 text-right">{lang === 'es' ? 'Saldo' : 'Balance'}</th></tr></thead>
                <tbody>
                  {adminWallets.map(w => (
                    <tr key={w.id} className="border-b border-pulse-border/50 hover:bg-pulse-elevated/50">
                      <td className="p-3">{w.user_info?.name || '-'}</td>
                      <td className="p-3 text-pulse-muted">{w.user_info?.email || '-'}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${w.user_info?.role === 'admin' ? 'bg-purple-500/20 text-purple-400' : w.user_info?.role === 'artist' ? 'bg-cyan-500/20 text-cyan-400' : w.user_info?.role === 'pluka' ? 'bg-pink-500/20 text-pink-400' : 'bg-gray-500/20 text-gray-400'}`}>{w.user_info?.role === 'admin' ? (lang === 'es' ? 'Dirección' : 'Owner') : w.user_info?.role === 'pluka' ? (lang === 'es' ? 'Gestión' : 'Operations') : w.user_info?.role === 'artist' ? (lang === 'es' ? 'Artista' : 'Artist') : (lang === 'es' ? 'Usuario' : 'User')}</span></td>
                      <td className="p-3 text-right font-bold text-amber-400">{(w.balance || 0).toLocaleString()} <span className="text-[9px] text-pulse-muted">corcheas</span><br/><span className="text-[10px] text-pulse-muted font-normal">{(w.balance_fiat || 0).toFixed(2)}EUR</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Donations Admin */}
        {activeTab === 'donations' && (
          <div data-testid="dash-donations">
            <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2"><Gift size={22} className="text-amber-400" /> {lang === 'es' ? 'Apoyos' : 'Supports'}</h2>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">Total Corcheas</p><p className="text-2xl font-bold text-amber-400">{(adminDonations.total_corcheas || adminDonations.total || 0).toLocaleString()}</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Comisiones' : 'Fees'}</p><p className="text-2xl font-bold text-green-400">{(adminDonations.total_fees_corcheas || adminDonations.total_fees || 0).toLocaleString()}</p></div>
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Num. Apoyos' : 'Num. Supports'}</p><p className="text-2xl font-bold text-pulse-primary">{(adminDonations.apoyos || adminDonations.donations || []).length}</p></div>
            </div>
            <div className="space-y-2">
              {(adminDonations.apoyos || adminDonations.donations || []).map(d => (
                <div key={d.id} className="flex items-center gap-3 p-3 bg-pulse-surface border border-pulse-border rounded-xl">
                  <span className="text-amber-400">&#9835;</span>
                  <div className="flex-1"><p className="text-sm"><span className="text-amber-400">{d.from_user_name}</span> &rarr; <span className="text-pulse-primary">{d.artist_name || d.recipient_name}</span></p><p className="text-[10px] text-pulse-muted">{d.message} {d.is_live && <span className="text-red-400 ml-1">LIVE</span>}</p></div>
                  <div className="text-right"><p className="text-sm font-bold text-amber-400">{d.amount_corcheas || d.amount || 0} <span className="text-[10px]">corcheas</span></p><p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Comision' : 'Fee'}: {d.fee_corcheas || d.fee || 0}</p></div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live Sessions Admin */}
        {activeTab === 'live' && (
          <div data-testid="dash-live">
            <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2"><Radio size={22} className="text-red-500" /> {lang === 'es' ? 'Sesiones en Vivo' : 'Live Sessions'}</h2>
            {adminLiveSessions.length === 0 ? (
              <p className="text-pulse-muted text-center py-12">{lang === 'es' ? 'No hay sesiones registradas' : 'No sessions recorded'}</p>
            ) : (
              <div className="space-y-2">
                {adminLiveSessions.map(s => (
                  <div key={s.id} className="flex items-center gap-3 p-4 bg-pulse-surface border border-pulse-border rounded-xl">
                    <div className={`w-3 h-3 rounded-full ${s.status === 'live' ? 'bg-red-500 animate-pulse' : 'bg-gray-500'}`} />
                    <div className="flex-1">
                      <p className="font-semibold">{s.artist_name} - {s.title}</p>
                      <p className="text-xs text-pulse-muted">{new Date(s.started_at).toLocaleString()} | {s.viewers} viewers | {(s.chat || []).length} msgs</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${s.status === 'live' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>{s.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* Escrow */}
        {activeTab === 'escrow' && (
          <div data-testid="dash-escrow">
            <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2"><CreditCard size={22} className="text-yellow-400" /> {lang === 'es' ? 'Escrow / Pagos' : 'Escrow / Payments'}</h2>
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-pulse-surface border border-yellow-500/20 rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Congelado' : 'Frozen'}</p><p className="text-2xl font-bold text-yellow-400">{escrowData.total_frozen?.toFixed(2)}€</p></div>
              <div className="bg-pulse-surface border border-green-500/20 rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Liberado' : 'Released'}</p><p className="text-2xl font-bold text-green-400">{escrowData.total_released?.toFixed(2)}€</p></div>
              <div className="bg-pulse-surface border border-purple-500/20 rounded-xl p-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'Plataforma (30%)' : 'Platform (30%)'}</p><p className="text-2xl font-bold text-purple-400">{escrowData.total_platform_earned?.toFixed(2)}€</p></div>
            </div>
            <div className="mb-4"><p className="text-xs text-pulse-muted">{lang === 'es' ? 'El 70% va al artista y queda congelado hasta confirmar el evento. El 30% restante corresponde a gestión y plataforma.' : '70% goes to the artist and stays frozen until the event is confirmed. The remaining 30% belongs to operations and platform.'}</p></div>
            {!isOwner && <p data-testid="escrow-readonly-message" className="text-xs text-yellow-300 mb-4">{lang === 'es' ? 'Modo supervisión: solo admin puede liberar o alterar este reparto fijo.' : 'Supervision mode: only admin can release or alter this fixed split.'}</p>}
            <h3 className="font-heading font-semibold mb-3">{lang === 'es' ? 'Congelados' : 'Frozen'}</h3>
            <div className="space-y-2 mb-6">
              {(escrowData.frozen || []).map(b => (
                <div key={b.id} className="flex items-center gap-3 p-3 bg-pulse-surface border border-yellow-500/20 rounded-xl">
                  <div className="flex-1"><p className="text-sm font-semibold">{b.artist_name}</p><p className="text-xs text-pulse-muted">{b.date} | {b.total_price}€ total</p></div>
                  <span className="text-yellow-400 text-sm font-bold">{b.artist_share}€</span>
                  {isOwner && <button onClick={async () => { await fetch(`${API}/api/pluka/release/${b.id}`, { method: 'POST', headers }); fetchAdminTab('escrow'); }} className="bg-green-500/20 text-green-400 px-3 py-1.5 rounded-lg text-xs font-medium">{lang === 'es' ? 'Liberar' : 'Release'}</button>}
                </div>
              ))}
              {(escrowData.frozen || []).length === 0 && <p className="text-pulse-muted text-sm text-center py-4">{lang === 'es' ? 'Todo liberado' : 'All released'}</p>}
            </div>
            <h3 className="font-heading font-semibold mb-3">{lang === 'es' ? 'Historial Liberados' : 'Released History'}</h3>
            <div className="space-y-2">
              {(escrowData.released || []).map(b => (
                <div key={b.id} className="flex items-center gap-3 p-3 bg-pulse-surface border border-green-500/10 rounded-xl">
                  <div className="flex-1"><p className="text-sm">{b.artist_name} - {b.date}</p></div>
                  <span className="text-green-400 text-xs">{b.artist_share}€ liberado</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notifications Admin */}
        {activeTab === 'notifications' && (
          <div data-testid="dash-notifications">
            <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2"><Bell size={22} className="text-pulse-primary" /> {lang === 'es' ? 'Enviar Notificacion' : 'Send Notification'}</h2>
            <div className="bg-pulse-surface border border-pulse-border rounded-xl p-6 max-w-xl">
              <div className="space-y-4">
                <div>
                  <label className="text-xs text-pulse-muted block mb-1">{lang === 'es' ? 'Destinatario' : 'Recipient'}</label>
                  <select value={notifForm.user_id} onChange={e => setNotifForm(f => ({ ...f, user_id: e.target.value }))} className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none">
                    <option value="all">{lang === 'es' ? 'Todos los usuarios' : 'All users'}</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-pulse-muted block mb-1">{lang === 'es' ? 'Titulo' : 'Title'}</label>
                  <input value={notifForm.title} onChange={e => setNotifForm(f => ({ ...f, title: e.target.value }))} className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs text-pulse-muted block mb-1">{lang === 'es' ? 'Mensaje' : 'Message'}</label>
                  <textarea value={notifForm.message} onChange={e => setNotifForm(f => ({ ...f, message: e.target.value }))} rows={3} className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                </div>
                <button onClick={sendAdminNotif} className="bg-pulse-primary text-black px-6 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2"><Send size={14} /> {lang === 'es' ? 'Enviar Notificacion' : 'Send Notification'}</button>
                {notifSent && <p className="text-xs text-green-400">{lang === 'es' ? 'Notificacion enviada!' : 'Notification sent!'}</p>}
              </div>
            </div>
          </div>
        )}


        {/* Content Reports (DMCA/DSA) */}
        {activeTab === 'reports' && (
          <div data-testid="dash-reports">
            <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2"><Flag size={22} className="text-red-400" /> {lang === 'es' ? 'Reportes de Contenido' : 'Content Reports'}</h2>
            <div className="grid grid-cols-4 gap-3 mb-6">
              {[
                { label: 'Total', val: contentReports.stats?.total || 0, color: 'white' },
                { label: lang === 'es' ? 'Pendientes' : 'Pending', val: contentReports.stats?.pending || 0, color: 'yellow-400' },
                { label: lang === 'es' ? 'Resueltos' : 'Resolved', val: contentReports.stats?.resolved || 0, color: 'green-400' },
                { label: lang === 'es' ? 'Descartados' : 'Dismissed', val: contentReports.stats?.dismissed || 0, color: 'pulse-muted' },
              ].map((s, i) => (
                <div key={i} className="bg-pulse-surface border border-pulse-border rounded-xl p-4 text-center">
                  <p className={`text-xl font-bold text-${s.color}`}>{s.val}</p>
                  <p className="text-[10px] text-pulse-muted">{s.label}</p>
                </div>
              ))}
            </div>
            {(contentReports.reports || []).length === 0 ? (
              <p className="text-center text-pulse-muted py-12">{lang === 'es' ? 'Sin reportes' : 'No reports'}</p>
            ) : (
              <div className="space-y-2">
                {(contentReports.reports || []).map(r => (
                  <div key={r.id} className={`p-4 rounded-xl border ${r.status === 'pending' ? 'border-yellow-500/20 bg-yellow-500/5' : 'border-pulse-border bg-pulse-surface'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' : r.status === 'resolved' ? 'bg-green-500/20 text-green-400' : 'bg-pulse-elevated text-pulse-muted'}`}>{r.status}</span>
                        <span className="text-xs bg-pulse-elevated px-2 py-0.5 rounded-full">{r.content_type}</span>
                        <span className="text-xs text-red-400">{r.reason}</span>
                      </div>
                      <span className="text-[10px] text-pulse-muted">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-pulse-muted mb-2">{r.description || r.reason}</p>
                    {r.status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={async () => {
                          await fetch(`${API}/api/admin/reports/${r.id}`, { method: 'PUT', headers, body: JSON.stringify({ status: 'resolved', action: 'remove_content' }) });
                          fetchReports();
                        }} className="text-xs bg-red-500/20 text-red-400 px-3 py-1 rounded-lg">{lang === 'es' ? 'Eliminar contenido' : 'Remove content'}</button>
                        <button onClick={async () => {
                          await fetch(`${API}/api/admin/reports/${r.id}`, { method: 'PUT', headers, body: JSON.stringify({ status: 'resolved', action: 'warn_user' }) });
                          fetchReports();
                        }} className="text-xs bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-lg">{lang === 'es' ? 'Advertir usuario' : 'Warn user'}</button>
                        <button onClick={async () => {
                          await fetch(`${API}/api/admin/reports/${r.id}`, { method: 'PUT', headers, body: JSON.stringify({ status: 'dismissed' }) });
                          fetchReports();
                        }} className="text-xs bg-pulse-elevated text-pulse-muted px-3 py-1 rounded-lg">{lang === 'es' ? 'Descartar' : 'Dismiss'}</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {/* Ad Command Center */}
        {activeTab === 'ads' && isOwner && (
          <div data-testid="dash-ads">
            <h2 className="font-heading font-bold text-2xl flex items-center gap-2 mb-4">
              <Zap size={22} className="text-amber-400" /> {lang === 'es' ? 'Centro de Publicidad' : 'Ad Center'}
            </h2>
            <AdCommandCenter />
          </div>
        )}

        {activeTab === 'legal_review' && isOwner && (
          <div data-testid="dash-legal-review">
            <LegalReviewPanel />
          </div>
        )}

        {activeTab === 'legal_ops' && isOwner && (
          <div data-testid="dash-legal-ops">
            <LegalOpsPanel />
          </div>
        )}

        {/* Command Center */}
        {activeTab === 'command_center' && isOwner && (
          <div data-testid="dash-command-center" className="space-y-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
                <Gauge size={22} className="text-pulse-primary" /> Command Center
              </h2>
              <button onClick={fetchCommandCenter} className="text-xs text-pulse-muted hover:text-pulse-primary flex items-center gap-1" data-testid="cc-refresh">
                <RefreshCw size={14} /> {lang === 'es' ? 'Actualizar' : 'Refresh'}
              </button>
            </div>

            {ccLoading && <p className="text-pulse-muted text-sm animate-pulse">{lang === 'es' ? 'Cargando datos...' : 'Loading...'}</p>}

            {/* Summary Cards */}
            {ccSummary && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cc-summary">
                <div className="bg-pulse-card border border-pulse-border rounded-xl p-4">
                  <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Llamadas API (7d)' : 'API Calls (7d)'}</p>
                  <p className="text-2xl font-bold text-white" data-testid="cc-total-calls">{ccSummary.total_api_calls}</p>
                </div>
                <div className="bg-pulse-card border border-pulse-border rounded-xl p-4">
                  <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Coste API (7d)' : 'API Cost (7d)'}</p>
                  <p className="text-2xl font-bold text-red-400" data-testid="cc-total-cost">${ccSummary.total_cost_usd?.toFixed(3)}</p>
                </div>
                <div className="bg-pulse-card border border-pulse-border rounded-xl p-4">
                  <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Tasa de exito' : 'Success Rate'}</p>
                  <p className={`text-2xl font-bold ${ccSummary.success_rate_pct >= 90 ? 'text-green-400' : ccSummary.success_rate_pct >= 70 ? 'text-yellow-400' : 'text-red-400'}`} data-testid="cc-success-rate">
                    {ccSummary.success_rate_pct}%
                  </p>
                </div>
                <div className="bg-pulse-card border border-pulse-border rounded-xl p-4">
                  <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Latencia media' : 'Avg Latency'}</p>
                  <p className="text-2xl font-bold text-cyan-400" data-testid="cc-avg-latency">{ccSummary.avg_latency_ms}ms</p>
                </div>
              </div>
            )}

            {/* Provider Health Semaphore */}
            <div className="bg-pulse-card border border-pulse-border rounded-xl p-5" data-testid="cc-providers">
              <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                <Activity size={18} className="text-pulse-primary" />
                {lang === 'es' ? 'Estado de Providers' : 'Provider Health'}
              </h3>
              {ccProviders.length === 0 && !ccLoading && (
                <p className="text-pulse-muted text-sm">{lang === 'es' ? 'Sin datos aun. Genera musica para ver estadisticas.' : 'No data yet. Generate music to see stats.'}</p>
              )}
              <div className="space-y-3">
                {ccProviders.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 bg-pulse-elevated rounded-lg px-4 py-3" data-testid={`cc-provider-${p.provider}`}>
                    <span className={`w-3 h-3 rounded-full flex-shrink-0 ${p.health === 'green' ? 'bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.5)]' : p.health === 'yellow' ? 'bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.5)]' : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]'}`} />
                    <span className="font-mono text-sm font-medium text-white min-w-[120px]">{p.provider}</span>
                    <span className="text-xs text-pulse-muted">{p.calls_24h} calls</span>
                    <span className="text-xs text-red-300">${p.cost_24h_usd?.toFixed(3)}</span>
                    <span className={`text-xs ${p.success_rate_pct >= 90 ? 'text-green-400' : 'text-red-400'}`}>{p.success_rate_pct}%</span>
                    <span className="text-xs text-cyan-400">{p.avg_latency_ms}ms</span>
                    {p.last_error_type && (
                      <span className="text-xs text-red-400 ml-auto truncate max-w-[200px]">{p.last_error_type}: {p.last_error_msg}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Activity Timeline */}
            {ccActivity.length > 0 && (
              <div className="bg-pulse-card border border-pulse-border rounded-xl p-5" data-testid="cc-activity">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <BarChart3 size={18} className="text-pulse-primary" />
                  {lang === 'es' ? 'Actividad (24h)' : 'Activity (24h)'}
                </h3>
                <div className="flex items-end gap-1 h-32">
                  {ccActivity.map((a, i) => {
                    const total = a.music + a.voice + a.llm;
                    const maxVal = Math.max(...ccActivity.map(x => x.music + x.voice + x.llm), 1);
                    const pct = (total / maxVal) * 100;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1" title={`${a.hour} — Music: ${a.music}, Voice: ${a.voice}, LLM: ${a.llm}, Cost: $${a.cost?.toFixed(3)}`}>
                        <div className="w-full bg-pulse-elevated rounded-t" style={{ height: `${Math.max(pct, 4)}%` }}>
                          <div className="w-full bg-gradient-to-t from-cyan-600 to-cyan-400 rounded-t h-full" />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-[10px] text-pulse-muted mt-1">
                  <span>{ccActivity[0]?.hour?.slice(11, 16)}</span>
                  <span>{ccActivity[ccActivity.length - 1]?.hour?.slice(11, 16)}</span>
                </div>
              </div>
            )}

            {/* Corcheas Economy */}
            {ccEconomy && (
              <div className="bg-pulse-card border border-pulse-border rounded-xl p-5" data-testid="cc-economy">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <DollarSign size={18} className="text-pulse-primary" />
                  {lang === 'es' ? 'Economia Corcheas' : 'Corcheas Economy'}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Circulacion total' : 'Total Circulation'}</p>
                    <p className="text-xl font-bold text-white">{ccEconomy.total_circulation?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Ganadas (7d)' : 'Earned (7d)'}</p>
                    <p className="text-xl font-bold text-green-400">+{ccEconomy.earned_7d?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Gastadas (7d)' : 'Spent (7d)'}</p>
                    <p className="text-xl font-bold text-red-400">-{ccEconomy.spent_7d?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Compradas (7d)' : 'Purchased (7d)'}</p>
                    <p className="text-xl font-bold text-cyan-400">{ccEconomy.purchased_7d_corcheas?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Revenue (7d)' : 'Revenue (7d)'}</p>
                    <p className="text-xl font-bold text-yellow-400">${ccEconomy.purchased_7d_usd?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Burn rate/dia' : 'Burn rate/day'}</p>
                    <p className="text-xl font-bold text-orange-400">{ccEconomy.burn_rate_daily}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Errors */}
            {ccErrors.length > 0 && (
              <div className="bg-pulse-card border border-pulse-border rounded-xl p-5" data-testid="cc-errors">
                <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                  <AlertCircle size={18} className="text-red-400" />
                  {lang === 'es' ? 'Errores recientes' : 'Recent Errors'}
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {ccErrors.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs bg-pulse-elevated rounded px-3 py-2">
                      <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />
                      <span className="text-pulse-muted">{e.timestamp?.slice(11, 19)}</span>
                      <span className="font-mono text-white">{e.provider}</span>
                      <span className="text-red-300">{e.error_type}</span>
                      <span className="text-pulse-muted truncate max-w-[300px]">{e.error_msg}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══ API Health Monitor ══ */}
        {activeTab === 'api_health' && isOwner && (
          <div data-testid="dash-api-health" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
                <Wifi size={22} className="text-pulse-primary" /> {lang === 'es' ? 'Estado de APIs Externas' : 'External API Health'}
              </h2>
              <button onClick={fetchApiHealth} data-testid="api-health-refresh" className="text-xs text-pulse-muted hover:text-pulse-primary flex items-center gap-1">
                <RefreshCw size={14} className={apiHealthLoading ? 'animate-spin' : ''} /> {lang === 'es' ? 'Verificar ahora' : 'Check now'}
              </button>
            </div>

            {apiHealthLoading && <p className="text-pulse-muted text-sm animate-pulse">{lang === 'es' ? 'Verificando APIs...' : 'Checking APIs...'}</p>}

            {apiHealth && (
              <>
                {/* Summary bar */}
                <div className="grid grid-cols-4 gap-3" data-testid="api-health-summary">
                  <div className="bg-pulse-surface border border-green-500/20 rounded-xl p-4 text-center">
                    <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Operativas' : 'Healthy'}</p>
                    <p className="text-2xl font-bold text-green-400">{apiHealth.summary?.ok || 0}</p>
                  </div>
                  <div className="bg-pulse-surface border border-yellow-500/20 rounded-xl p-4 text-center">
                    <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Aviso' : 'Warning'}</p>
                    <p className="text-2xl font-bold text-yellow-400">{apiHealth.summary?.warning || 0}</p>
                  </div>
                  <div className="bg-pulse-surface border border-red-500/20 rounded-xl p-4 text-center">
                    <p className="text-xs text-pulse-muted">Error</p>
                    <p className="text-2xl font-bold text-red-400">{apiHealth.summary?.error || 0}</p>
                  </div>
                  <div className="bg-pulse-surface border border-white/10 rounded-xl p-4 text-center">
                    <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Sin config' : 'Missing'}</p>
                    <p className="text-2xl font-bold text-white/40">{apiHealth.summary?.missing || 0}</p>
                  </div>
                </div>

                {/* Provider cards */}
                <div className="space-y-3">
                  {(apiHealth.providers || []).map((p, i) => (
                    <div key={i} data-testid={`api-provider-${i}`} className={`rounded-xl border p-4 flex items-center gap-4 ${
                      p.status === 'ok' ? 'border-green-500/20 bg-green-500/5' :
                      p.status === 'warning' ? 'border-yellow-500/20 bg-yellow-500/5' :
                      p.status === 'error' ? 'border-red-500/30 bg-red-500/5' :
                      'border-white/10 bg-white/[0.02]'
                    }`}>
                      <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                        p.status === 'ok' ? 'bg-green-500' :
                        p.status === 'warning' ? 'bg-yellow-500 animate-pulse' :
                        p.status === 'error' ? 'bg-red-500 animate-pulse' :
                        'bg-white/20'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{p.provider}</p>
                        <p className="text-xs text-pulse-muted">{p.message}</p>
                      </div>
                      {p.balance != null && (
                        <div className="text-right">
                          <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Saldo' : 'Balance'}</p>
                          <p className="text-sm font-bold text-amber-400">{typeof p.balance === 'number' ? `$${p.balance.toFixed(2)}` : p.balance}</p>
                        </div>
                      )}
                      <div className="text-right">
                        <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Latencia' : 'Latency'}</p>
                        <p className="text-xs font-mono">{p.latency_ms}ms</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Recent errors */}
                {(apiHealth.recent_errors || []).length > 0 && (
                  <div className="bg-pulse-surface border border-red-500/20 rounded-xl p-5">
                    <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-red-400">
                      <AlertCircle size={16} /> {lang === 'es' ? 'Errores ultimas 24h' : 'Errors last 24h'}
                    </h3>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {apiHealth.recent_errors.map((e, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs bg-pulse-elevated rounded px-3 py-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                          <span className="text-pulse-muted">{e.timestamp?.slice(11, 19)}</span>
                          <span className="font-mono text-white">{e.provider}</span>
                          <span className="text-red-300 truncate">{e.error}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ══ Platform Balance / Financials ══ */}
        {activeTab === 'platform_balance' && isOwner && (
          <div data-testid="dash-platform-balance" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-bold text-2xl flex items-center gap-2">
                <PiggyBank size={22} className="text-green-400" /> {lang === 'es' ? 'Balance de Plataforma' : 'Platform Balance'}
              </h2>
              <div className="flex gap-1">
                {['7d','30d','90d','365d','all'].map(p => (
                  <button key={p} onClick={() => setPfPeriod(p)} className={`px-3 py-1 rounded-lg text-xs ${pfPeriod === p ? 'bg-pulse-primary text-black' : 'bg-pulse-surface border border-pulse-border'}`}>
                    {p === 'all' ? 'Todo' : p}
                  </button>
                ))}
              </div>
            </div>

            {pfLoading && <p className="text-pulse-muted text-sm animate-pulse">{lang === 'es' ? 'Calculando...' : 'Calculating...'}</p>}

            {platformFinancials && (
              <>
                {/* Health indicator */}
                <div className={`rounded-xl border p-4 flex items-center gap-3 ${
                  platformFinancials.health_score === 'saludable' ? 'border-green-500/20 bg-green-500/5' :
                  platformFinancials.health_score === 'precaucion' ? 'border-yellow-500/20 bg-yellow-500/5' :
                  'border-red-500/20 bg-red-500/5'
                }`}>
                  <div className={`w-3 h-3 rounded-full ${
                    platformFinancials.health_score === 'saludable' ? 'bg-green-500' :
                    platformFinancials.health_score === 'precaucion' ? 'bg-yellow-500 animate-pulse' :
                    'bg-red-500 animate-pulse'
                  }`} />
                  <p className="text-sm font-semibold">
                    {lang === 'es' ? 'Estado financiero: ' : 'Financial health: '}
                    <span className={
                      platformFinancials.health_score === 'saludable' ? 'text-green-400' :
                      platformFinancials.health_score === 'precaucion' ? 'text-yellow-400' :
                      'text-red-400'
                    }>
                      {platformFinancials.health_score === 'saludable' ? (lang === 'es' ? 'Saludable' : 'Healthy') :
                       platformFinancials.health_score === 'precaucion' ? (lang === 'es' ? 'Precaucion' : 'Caution') :
                       (lang === 'es' ? 'Deficit' : 'Deficit')}
                    </span>
                  </p>
                </div>

                {/* Key metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-pulse-surface border border-green-500/20 rounded-xl p-4">
                    <p className="text-[10px] text-pulse-muted mb-1">{lang === 'es' ? 'Volumen bruto' : 'Gross volume'}</p>
                    <p className="text-xl font-bold text-green-400">{platformFinancials.gross_revenue}€</p>
                  </div>
                  <div className="bg-pulse-surface border border-cyan-500/20 rounded-xl p-4">
                    <p className="text-[10px] text-pulse-muted mb-1">{lang === 'es' ? 'Comisiones netas' : 'Net commissions'}</p>
                    <p className="text-xl font-bold text-cyan-400">{platformFinancials.net_revenue}€</p>
                  </div>
                  <div className="bg-pulse-surface border border-orange-500/20 rounded-xl p-4">
                    <p className="text-[10px] text-pulse-muted mb-1">{lang === 'es' ? 'Costes API' : 'API costs'}</p>
                    <p className="text-xl font-bold text-orange-400">${platformFinancials.total_api_cost}</p>
                  </div>
                  <div className="bg-pulse-surface border border-purple-500/20 rounded-xl p-4">
                    <p className="text-[10px] text-pulse-muted mb-1">{lang === 'es' ? 'Beneficio' : 'Profit'}</p>
                    <p className={`text-xl font-bold ${platformFinancials.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>{platformFinancials.profit}€</p>
                  </div>
                </div>

                {/* Commission breakdown */}
                <div className="bg-pulse-surface border border-pulse-border rounded-xl p-5">
                  <h3 className="font-semibold text-sm mb-4">{lang === 'es' ? 'Desglose de comisiones' : 'Commission breakdown'}</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Apoyos + Bookings' : 'Support + Bookings'}</p>
                      <p className="text-sm font-bold">{platformFinancials.commissions?.general_rate} = {platformFinancials.commissions?.general_commission}€</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pulse-muted">Pulse Producer</p>
                      <p className="text-sm font-bold text-amber-400">{platformFinancials.commissions?.producer_rate} = {platformFinancials.commissions?.producer_commission}€</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Total comisiones' : 'Total commissions'}</p>
                      <p className="text-sm font-bold text-cyan-400">{platformFinancials.commissions?.total_commission}€</p>
                    </div>
                  </div>
                </div>

                {/* Bank reserve */}
                <div className="bg-pulse-surface border border-green-500/20 rounded-xl p-5">
                  <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-green-400">
                    <PiggyBank size={16} /> {lang === 'es' ? 'Reserva Bancaria (Ahorro)' : 'Bank Reserve (Savings)'}
                  </h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Tasa' : 'Rate'}</p>
                      <p className="text-sm font-bold">{platformFinancials.bank_reserve?.rate}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Acumulado' : 'Accumulated'}</p>
                      <p className="text-sm font-bold text-green-400">{platformFinancials.bank_reserve?.amount}€</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Presupuesto operativo' : 'Operational budget'}</p>
                      <p className="text-sm font-bold text-white">{platformFinancials.operational_budget}€</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-pulse-muted mt-2">{platformFinancials.bank_reserve?.description}</p>
                </div>

                {/* Volume breakdown */}
                <div className="bg-pulse-surface border border-pulse-border rounded-xl p-5">
                  <h3 className="font-semibold text-sm mb-4">{lang === 'es' ? 'Volumen por categoria' : 'Volume by category'}</h3>
                  <div className="space-y-3">
                    {Object.entries(platformFinancials.breakdown || {}).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium capitalize">{key === 'donations' ? (lang === 'es' ? 'Apoyos' : 'Support') : key === 'bookings' ? 'Bookings' : 'Producer'}</p>
                          <p className="text-[10px] text-pulse-muted">{val.count} {lang === 'es' ? 'operaciones' : 'operations'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">{val.volume}€</p>
                          <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Comision' : 'Commission'}: {val.commission}€</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* API Costs breakdown */}
                {(platformFinancials.api_costs || []).length > 0 && (
                  <div className="bg-pulse-surface border border-orange-500/20 rounded-xl p-5">
                    <h3 className="font-semibold text-sm mb-3 text-orange-400">{lang === 'es' ? 'Costes API por proveedor' : 'API costs by provider'}</h3>
                    <div className="space-y-2">
                      {platformFinancials.api_costs.map((a, i) => (
                        <div key={i} className="flex items-center justify-between text-xs">
                          <span className="font-mono">{a.provider}</span>
                          <div className="flex gap-4">
                            <span className="text-pulse-muted">{a.calls} calls</span>
                            {a.errors > 0 && <span className="text-red-400">{a.errors} errors</span>}
                            <span className="font-bold text-orange-400">${a.cost_usd}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reinvestment */}
                <div className="bg-pulse-surface border border-cyan-500/20 rounded-xl p-5">
                  <h3 className="font-semibold text-sm mb-3 text-cyan-400">{lang === 'es' ? 'Reinversion y Sostenibilidad' : 'Reinvestment & Sustainability'}</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Disponible para reinvertir' : 'Available to reinvest'}</p>
                      <p className={`text-sm font-bold ${platformFinancials.reinvestment?.available_for_reinvestment >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {platformFinancials.reinvestment?.available_for_reinvestment}€
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Reserva mensual sugerida' : 'Suggested monthly reserve'}</p>
                      <p className="text-sm font-bold text-yellow-400">${platformFinancials.reinvestment?.suggested_monthly_reserve}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-pulse-muted">{lang === 'es' ? 'Ratio sostenibilidad' : 'Sustainability ratio'}</p>
                      <p className={`text-sm font-bold ${platformFinancials.reinvestment?.sustainability_ratio >= 1 ? 'text-green-400' : 'text-red-400'}`}>
                        {platformFinancials.reinvestment?.sustainability_ratio}x
                      </p>
                    </div>
                  </div>
                </div>

                {/* Wallet totals */}
                <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-pulse-muted">{lang === 'es' ? 'Total en monederos de usuarios' : 'Total in user wallets'}</p>
                    <p className="text-lg font-bold">{platformFinancials.wallets?.total_balance} <span className="text-xs text-pulse-muted">corcheas ({platformFinancials.wallets?.count} wallets)</span></p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* System Config */}
        {activeTab === 'system' && isOwner && (
          <div data-testid="dash-system">
            <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2"><Sliders size={22} className="text-pulse-primary" /> {lang === 'es' ? 'Control del Sistema' : 'System Control'}</h2>
            {systemConfig ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {[
                    { key: 'maintenance_mode', label: lang === 'es' ? 'Modo mantenimiento' : 'Maintenance mode', color: 'red' },
                    { key: 'registration_enabled', label: lang === 'es' ? 'Registro abierto' : 'Registration open', color: 'green' },
                    { key: 'donations_enabled', label: lang === 'es' ? 'Apoyos' : 'Support', color: 'yellow' },
                    { key: 'live_enabled', label: 'Live', color: 'red' },
                    { key: 'bookings_enabled', label: lang === 'es' ? 'Contrataciones' : 'Bookings', color: 'purple' },
                    { key: 'ai_enabled', label: 'AI', color: 'cyan' },
                    { key: 'wallet_deposits_enabled', label: lang === 'es' ? 'Depositos' : 'Deposits', color: 'green' },
                    { key: 'wallet_withdrawals_enabled', label: lang === 'es' ? 'Retiros' : 'Withdrawals', color: 'orange' },
                  ].map(item => (
                    <button key={item.key} onClick={() => {
                      const newVal = !systemConfig[item.key];
                      setSystemConfig(c => ({ ...c, [item.key]: newVal }));
                      fetch(`${API}/api/admin/system`, { method: 'PUT', headers, body: JSON.stringify({ [item.key]: newVal }) });
                    }} className={`p-4 rounded-xl border transition-all ${systemConfig[item.key] ? `border-${item.color}-500/30 bg-${item.color}-500/10` : 'border-pulse-border bg-pulse-surface opacity-50'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-pulse-muted">{item.label}</span>
                        <div className={`w-8 h-4 rounded-full relative transition-all ${systemConfig[item.key] ? 'bg-green-500' : 'bg-gray-600'}`}>
                          <div className={`absolute w-3 h-3 rounded-full bg-white top-0.5 transition-all ${systemConfig[item.key] ? 'right-0.5' : 'left-0.5'}`} />
                        </div>
                      </div>
                      <p className={`text-sm font-bold ${systemConfig[item.key] ? 'text-green-400' : 'text-red-400'}`}>{systemConfig[item.key] ? 'ON' : 'OFF'}</p>
                    </button>
                  ))}
                </div>
                <div className="bg-pulse-surface border border-pulse-border rounded-xl p-4 space-y-3">
                  <h3 className="text-sm font-semibold">{lang === 'es' ? 'Limites financieros' : 'Financial limits'}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { key: 'min_withdrawal', label: lang === 'es' ? 'Min liquidacion' : 'Min liquidation' },
                      { key: 'max_withdrawal', label: lang === 'es' ? 'Max liquidacion' : 'Max liquidation' },
                      { key: 'min_donation', label: lang === 'es' ? 'Min apoyo' : 'Min support' },
                      { key: 'max_donation', label: lang === 'es' ? 'Max apoyo' : 'Max support' },
                    ].map(item => (
                      <div key={item.key}>
                        <label className="text-[10px] text-pulse-muted">{item.label}</label>
                        <input type="number" value={systemConfig[item.key] || 0} onChange={e => {
                          const val = parseFloat(e.target.value);
                          setSystemConfig(c => ({ ...c, [item.key]: val }));
                        }} onBlur={() => fetch(`${API}/api/admin/system`, { method: 'PUT', headers, body: JSON.stringify({ [item.key]: systemConfig[item.key] }) })}
                        className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm" />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { key: 'booking_commission', label: lang === 'es' ? 'Comision booking' : 'Booking commission' },
                      { key: 'pluka_share', label: lang === 'es' ? 'Gestión %' : 'Operations %' },
                      { key: 'admin_share', label: lang === 'es' ? 'Plataforma %' : 'Platform %' },
                    ].map(item => (
                      <div key={item.key}>
                        <label className="text-[10px] text-pulse-muted">{item.label}</label>
                        <div className="flex items-center gap-2">
                          <input type="number" step="0.01" min="0" max="1" value={systemConfig[item.key] || 0} onChange={e => {
                            const val = parseFloat(e.target.value);
                            setSystemConfig(c => ({ ...c, [item.key]: val }));
                          }} onBlur={() => fetch(`${API}/api/admin/system`, { method: 'PUT', headers, body: JSON.stringify({ [item.key]: systemConfig[item.key] }) })}
                          className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2 text-sm" />
                          <span className="text-xs text-pulse-primary font-bold">{((systemConfig[item.key] || 0) * 100).toFixed(0)}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" /></div>
            )}
          </div>
        )}

        {/* Audit Log */}
        {activeTab === 'audit' && isOwner && (
          <div data-testid="dash-audit">
            <h2 className="font-heading font-bold text-2xl mb-6 flex items-center gap-2"><FileText size={22} className="text-orange-400" /> {lang === 'es' ? 'Registro de Auditoria' : 'Audit Log'}</h2>
            {auditLogs.length === 0 ? (
              <p className="text-pulse-muted text-center py-12">{lang === 'es' ? 'Sin registros de auditoria' : 'No audit logs'}</p>
            ) : (
              <div className="bg-pulse-surface border border-pulse-border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-pulse-border text-xs text-pulse-muted"><th className="p-3 text-left">{lang === 'es' ? 'Fecha' : 'Date'}</th><th className="p-3 text-left">{lang === 'es' ? 'Cuenta' : 'Account'}</th><th className="p-3 text-left">{lang === 'es' ? 'Accion' : 'Action'}</th><th className="p-3 text-left">{lang === 'es' ? 'Objetivo' : 'Target'}</th><th className="p-3 text-left">{lang === 'es' ? 'Detalles' : 'Details'}</th></tr></thead>
                  <tbody>
                    {auditLogs.map(log => (
                      <tr key={log.id} className="border-b border-pulse-border/30 hover:bg-pulse-elevated/50">
                        <td className="p-3 text-xs text-pulse-muted whitespace-nowrap">{new Date(log.created_at).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')}</td>
                        <td className="p-3 text-xs">{log.admin_info?.name || log.admin_info?.email || '-'}</td>
                        <td className="p-3"><span className="text-xs bg-pulse-elevated px-2 py-0.5 rounded-full">{log.action}</span></td>
                        <td className="p-3 text-xs">{log.target}</td>
                        <td className="p-3 text-[10px] text-pulse-muted max-w-[200px] truncate">{JSON.stringify(log.details)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Legal Archive — admin + pluka only */}
        {activeTab === 'legal_archive' && !isModeratorOnly && (
          <LegalArchivePanel token={token} lang={lang} />
        )}

        {/* Settings */}
        {activeTab === 'settings' && isSettingsOwner && (
          <div data-testid="dash-settings">
            <h2 className="font-heading font-bold text-2xl mb-6">{t('dashboard.settings')}</h2>
            <p className="text-xs text-pulse-muted mb-4 max-w-2xl">
              {lang === 'es'
                ? 'Las claves API solo pueden verlas y editarlas la cuenta directiva. Los demás administradores ven valores enmascarados y no pueden guardar cambios aquí.'
                : 'Only the director account can view and edit full API keys. Other admins see masked values and cannot save changes here.'}
            </p>
            
            <div className="space-y-6">
              {/* API Keys - Organized by category */}
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                  <Key size={18} className="text-pulse-primary" /> AI & Auth
                </h3>
                <div className="space-y-4">
                  {[
                    { key: 'openai_key', label: 'OpenAI API Key', placeholder: 'sk-...', field: 'openai' },
                    { key: 'claude_key', label: 'Claude API Key', placeholder: 'sk-ant-...', field: 'claude' },
                    { key: 'anthropic_api_key', label: 'Anthropic API Key (Pulse Producer)', placeholder: 'sk-ant-...', field: 'anthropic' },
                    { key: 'ops_admin_claude_model', label: 'Ops Admin Claude Model', placeholder: 'claude-sonnet-4-20250514', field: 'ops_claude_model', isText: true },
                    { key: 'ops_openai_fallback_model', label: 'Ops OpenAI Fallback Model', placeholder: 'gpt-4o', field: 'ops_openai_fallback_model', isText: true },
                    { key: 'ops_sentinel_openai_fallback_enabled', label: 'Ops Sentinel OpenAI Fallback Enabled (true/false)', placeholder: 'true', field: 'ops_sentinel_openai_fallback_enabled', isText: true },
                    { key: 'ops_github_repo_url', label: 'Ops GitHub Repo URL', placeholder: 'https://github.com/org/repo', field: 'ops_repo_url', isText: true },
                    { key: 'ops_github_repo_slug', label: 'Ops GitHub Repo Slug (optional)', placeholder: 'owner/repo', field: 'ops_repo_slug', isText: true },
                    { key: 'ops_github_api_token', label: 'Ops GitHub API Token (PR/Merge)', placeholder: 'ghp_... / github_pat_...', field: 'ops_github_api' },
                    { key: 'ops_github_app_id', label: 'Ops GitHub App ID', placeholder: '123456', field: 'ops_github_app_id', isText: true },
                    { key: 'ops_github_app_installation_id', label: 'Ops GitHub App Installation ID', placeholder: '7890123', field: 'ops_github_app_installation_id', isText: true },
                    { key: 'ops_github_app_private_key', label: 'Ops GitHub App Private Key (PEM)', placeholder: '-----BEGIN PRIVATE KEY-----', field: 'ops_github_app_private_key' },
                    { key: 'ops_code_ai_repo_path', label: 'Ops Code AI Repo Path', placeholder: '/workspace', field: 'ops_repo_path', isText: true },
                    { key: 'ops_code_ai_base_branch', label: 'Ops Code AI Base Branch', placeholder: 'main', field: 'ops_code_ai_base_branch', isText: true },
                    { key: 'ops_code_ai_default_merge_method', label: 'Ops Code AI Default Merge Method', placeholder: 'squash', field: 'ops_code_ai_default_merge_method', isText: true },
                    { key: 'suno_api_key', label: 'Suno API Key (Music Gen)', placeholder: 'Bearer key...', field: 'suno' },
                    { key: 'fish_audio_api_key', label: 'Fish Audio API Key (Voice)', placeholder: 'fa-...', field: 'fish_audio' },
                    { key: 'udio_api_key', label: 'Udio API Key (Music Gen)', placeholder: 'udio-...', field: 'udio' },
                    { key: 'replicate_api_token', label: 'Replicate Token (MusicGen)', placeholder: 'r8_...', field: 'replicate' },
                    { key: 'fal_ai_api_key', label: 'Fal.ai API Key (MiniMax/ElevenLabs Music)', placeholder: 'fal-key-...', field: 'fal_ai' },
                    { key: 'google_client_id', label: 'Google OAuth Client ID', placeholder: '*.apps.googleusercontent.com', field: 'google_client' },
                    { key: 'google_client_secret', label: 'Google OAuth Client Secret', placeholder: 'GOCSPX-...', field: 'google_secret' },
                    { key: 'google_maps_key', label: 'Google Maps API Key (JS / Places: enable billing + required APIs; key restrictions by HTTP referrers in prod)', placeholder: 'AIza...', field: 'google' },
                  ].map(item => (
                    <div key={item.key}>
                      <label className="text-xs text-pulse-muted block mb-1">{item.label}</label>
                      <div className="flex gap-2">
                        <input data-testid={`setting-${item.key}`} type={item.isText ? 'text' : (showKeys[item.field] ? 'text' : 'password')} value={settingsForm[item.key] || ''} onChange={e => setSettingsForm(f => ({...f, [item.key]: e.target.value}))} placeholder={item.placeholder} className="flex-1 bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                        {!item.isText && <button onClick={() => setShowKeys(k => ({...k, [item.field]: !k[item.field]}))} className="p-2 text-pulse-muted hover:text-white"><Eye size={16} /></button>}
                      </div>
                    </div>
                  ))}
                  <p className="text-[10px] text-pulse-muted leading-relaxed">
                    {lang === 'es'
                      ? 'El mapa de /events usa capas de OpenStreetMap (gratis, global). No hay corte a “un par de ciudades” en el código. La clave de Google sirve para futura geolocalización (autocomplete) cuando la conectes en el front. Google no limita el mundo; limitan claves, facturación y productos (Maps/Places) en Google Cloud.'
                      : 'The /events map uses OpenStreetMap tiles (free, global). The code does not restrict the map to a few cities. The Google key is for future geocoding (autocomplete) or JS Maps when you wire the front. Google is worldwide; keys, billing, and which APIs you enable in Cloud Console are what matter.'}
                  </p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-pulse-muted block mb-1">PAYMENT_RAIL</label>
                      <input
                        data-testid="setting-PAYMENT_RAIL"
                        type="text"
                        value={settingsForm.PAYMENT_RAIL ?? 'wallet'}
                        onChange={e => setSettingsForm(f => ({ ...f, PAYMENT_RAIL: e.target.value }))}
                        placeholder="wallet | stripe_connect"
                        className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-pulse-muted block mb-1">BOOKING_V2 (0/1)</label>
                      <input
                        data-testid="setting-BOOKING_V2"
                        type="number"
                        min="0"
                        max="1"
                        value={settingsForm.BOOKING_V2 === undefined || settingsForm.BOOKING_V2 === '' ? 0 : settingsForm.BOOKING_V2}
                        onChange={e => setSettingsForm(f => ({ ...f, BOOKING_V2: parseInt(e.target.value, 10) || 0 }))}
                        className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-pulse-muted block mb-1" title="Botón V2: Continuar a contratación (sin pago)">
                        BOOKING_PROCEED_TO_FORMAL (0/1)
                      </label>
                      <input
                        data-testid="setting-BOOKING_PROCEED_TO_FORMAL"
                        type="number"
                        min="0"
                        max="1"
                        value={settingsForm.BOOKING_PROCEED_TO_FORMAL === undefined || settingsForm.BOOKING_PROCEED_TO_FORMAL === '' ? 0 : settingsForm.BOOKING_PROCEED_TO_FORMAL}
                        onChange={e => setSettingsForm(f => ({ ...f, BOOKING_PROCEED_TO_FORMAL: parseInt(e.target.value, 10) || 0 }))}
                        className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-3 py-2.5 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-pulse-muted mb-2 flex items-center gap-1">
                      <Globe size={12} className="text-cyan-400" /> {lang === 'es' ? 'Vista de mapa /eventos' : 'Events map view'}
                    </p>
                    <div className="grid sm:grid-cols-3 gap-2">
                      <div>
                        <label className="text-[10px] text-pulse-muted">default_map_lat</label>
                        <input
                          data-testid="setting-default_map_lat"
                          type="number"
                          step="any"
                          value={settingsForm.default_map_lat ?? 20}
                          onChange={e => setSettingsForm(f => ({ ...f, default_map_lat: parseFloat(e.target.value) }))}
                          className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-2 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-pulse-muted">default_map_lng</label>
                        <input
                          data-testid="setting-default_map_lng"
                          type="number"
                          step="any"
                          value={settingsForm.default_map_lng ?? 0}
                          onChange={e => setSettingsForm(f => ({ ...f, default_map_lng: parseFloat(e.target.value) }))}
                          className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-2 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-pulse-muted">default_map_zoom</label>
                        <input
                          data-testid="setting-default_map_zoom"
                          type="number"
                          min="1"
                          max="18"
                          value={settingsForm.default_map_zoom ?? 2}
                          onChange={e => setSettingsForm(f => ({ ...f, default_map_zoom: parseInt(e.target.value, 10) || 2 }))}
                          className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-2 py-2 text-sm"
                        />
                      </div>
                    </div>
                    <div className="mt-2 grid sm:grid-cols-1 gap-2">
                      <div>
                        <label className="text-[10px] text-pulse-muted">events_map_caption_es</label>
                        <input
                          type="text"
                          value={settingsForm.events_map_caption_es || ''}
                          onChange={e => setSettingsForm(f => ({ ...f, events_map_caption_es: e.target.value }))}
                          className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-2 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-pulse-muted">events_map_caption_en</label>
                        <input
                          type="text"
                          value={settingsForm.events_map_caption_en || ''}
                          onChange={e => setSettingsForm(f => ({ ...f, events_map_caption_en: e.target.value }))}
                          className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-2 py-2 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-pulse-surface border border-cyan-500/20 rounded-xl p-6 space-y-5">
                <div>
                  <h3 className="font-heading font-semibold text-lg mb-2 flex items-center gap-2">
                    <Shield size={18} className="text-cyan-300" /> {lang === 'es' ? 'Ownership verification' : 'Ownership verification'}
                  </h3>
                  <p className="text-xs text-pulse-muted">{lang === 'es' ? 'Estas claves se leen dinámicamente desde admin_settings. Si faltan, el sistema entra en modo degradado sin romper subidas.' : 'These keys are read dynamically from admin_settings. If they are missing, uploads still work in degraded mode.'}</p>
                </div>

                <div className="rounded-2xl border border-cyan-500/25 bg-cyan-500/5 p-4 space-y-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200 mb-2">{lang === 'es' ? 'Punto único OAuth' : 'OAuth single source'}</p>
                    <label className="text-xs text-pulse-muted block mb-1">OAUTH_BASE_URL</label>
                    <input
                      data-testid="setting-oauth-base-url"
                      type="text"
                      value={settingsForm.OAUTH_BASE_URL || ''}
                      onChange={e => setSettingsForm(f => ({ ...f, OAUTH_BASE_URL: e.target.value }))}
                      placeholder={window.location.origin}
                      className="w-full bg-pulse-surface border border-cyan-500/20 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 text-xs">
                    <div data-testid="oauth-effective-spotify-uri" className="rounded-xl border border-pulse-border bg-pulse-surface px-4 py-3">
                      <p className="text-pulse-muted mb-1">Spotify callback</p>
                      <p className="text-cyan-200 break-all">{`${(settingsForm.OAUTH_BASE_URL || window.location.origin).replace(/\/$/, '')}/api/verification/oauth/spotify/callback`}</p>
                    </div>
                    <div data-testid="oauth-effective-google-uri" className="rounded-xl border border-pulse-border bg-pulse-surface px-4 py-3">
                      <p className="text-pulse-muted mb-1">Google callback</p>
                      <p className="text-cyan-200 break-all">{`${(settingsForm.OAUTH_BASE_URL || window.location.origin).replace(/\/$/, '')}/api/verification/oauth/google/callback`}</p>
                    </div>
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-5">
                  <div className="space-y-4 rounded-2xl border border-pulse-border bg-pulse-elevated/60 p-4">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-pulse-muted mb-3">ACRCloud</p>
                      {[ 
                        { key: 'ACRCLOUD_HOST', label: 'ACRCLOUD_HOST', placeholder: 'identify-eu-west-1.acrcloud.com', field: 'acr_host', isText: true },
                        { key: 'ACRCLOUD_ACCESS_KEY', label: 'ACRCLOUD_ACCESS_KEY', placeholder: 'acr_...', field: 'acr_key' },
                        { key: 'ACRCLOUD_ACCESS_SECRET', label: 'ACRCLOUD_ACCESS_SECRET', placeholder: 'secret...', field: 'acr_secret' },
                      ].map(item => (
                        <div key={item.key}>
                          <label className="text-xs text-pulse-muted block mb-1">{item.label}</label>
                          <div className="flex gap-2">
                            <input data-testid={`setting-${item.key.toLowerCase()}`} type={item.isText ? 'text' : (showKeys[item.field] ? 'text' : 'password')} value={settingsForm[item.key] || ''} onChange={e => setSettingsForm(f => ({ ...f, [item.key]: e.target.value }))} placeholder={item.placeholder} className="flex-1 bg-pulse-surface border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                            {!item.isText && <button data-testid={`toggle-${item.key.toLowerCase()}`} onClick={() => setShowKeys(k => ({ ...k, [item.field]: !k[item.field] }))} className="p-2 text-pulse-muted hover:text-white"><Eye size={16} /></button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4 rounded-2xl border border-pulse-border bg-pulse-elevated/60 p-4">
                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-pulse-muted mb-3">Spotify OAuth</p>
                      {[
                        { key: 'SPOTIFY_CLIENT_ID', label: 'SPOTIFY_CLIENT_ID', placeholder: 'spotify client id', field: 'spotify_client', isText: true },
                        { key: 'SPOTIFY_CLIENT_SECRET', label: 'SPOTIFY_CLIENT_SECRET', placeholder: 'spotify secret', field: 'spotify_secret' },
                        { key: 'SPOTIFY_REDIRECT_URI', label: 'SPOTIFY_REDIRECT_URI', placeholder: `${API}/api/verification/oauth/spotify/callback`, field: 'spotify_redirect', isText: true, readOnly: true },
                      ].map(item => (
                        <div key={item.key}>
                          <label className="text-xs text-pulse-muted block mb-1">{item.label}</label>
                          <div className="flex gap-2">
                            <input data-testid={`setting-${item.key.toLowerCase()}`} readOnly={item.readOnly} type={item.isText ? 'text' : (showKeys[item.field] ? 'text' : 'password')} value={item.readOnly ? `${(settingsForm.OAUTH_BASE_URL || window.location.origin).replace(/\/$/, '')}/api/verification/oauth/spotify/callback` : (settingsForm[item.key] || '')} onChange={e => setSettingsForm(f => ({ ...f, [item.key]: e.target.value }))} placeholder={item.placeholder} className={`flex-1 bg-pulse-surface border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none ${item.readOnly ? 'text-cyan-200 cursor-not-allowed' : ''}`} />
                            {!item.isText && <button data-testid={`toggle-${item.key.toLowerCase()}`} onClick={() => setShowKeys(k => ({ ...k, [item.field]: !k[item.field] }))} className="p-2 text-pulse-muted hover:text-white"><Eye size={16} /></button>}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-pulse-muted mb-3">Google / YouTube OAuth</p>
                      {[
                        { key: 'GOOGLE_CLIENT_ID', label: 'GOOGLE_CLIENT_ID', placeholder: '*.apps.googleusercontent.com', field: 'google_oauth_id', isText: true },
                        { key: 'GOOGLE_CLIENT_SECRET', label: 'GOOGLE_CLIENT_SECRET', placeholder: 'GOCSPX-...', field: 'google_oauth_secret' },
                        { key: 'GOOGLE_REDIRECT_URI', label: 'GOOGLE_REDIRECT_URI', placeholder: `${API}/api/verification/oauth/google/callback`, field: 'google_oauth_redirect', isText: true, readOnly: true },
                      ].map(item => (
                        <div key={item.key}>
                          <label className="text-xs text-pulse-muted block mb-1">{item.label}</label>
                          <div className="flex gap-2">
                            <input data-testid={`setting-${item.key.toLowerCase()}`} readOnly={item.readOnly} type={item.isText ? 'text' : (showKeys[item.field] ? 'text' : 'password')} value={item.readOnly ? `${(settingsForm.OAUTH_BASE_URL || window.location.origin).replace(/\/$/, '')}/api/verification/oauth/google/callback` : (settingsForm[item.key] || '')} onChange={e => setSettingsForm(f => ({ ...f, [item.key]: e.target.value }))} placeholder={item.placeholder} className={`flex-1 bg-pulse-surface border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none ${item.readOnly ? 'text-cyan-200 cursor-not-allowed' : ''}`} />
                            {!item.isText && <button data-testid={`toggle-${item.key.toLowerCase()}`} onClick={() => setShowKeys(k => ({ ...k, [item.field]: !k[item.field] }))} className="p-2 text-pulse-muted hover:text-white"><Eye size={16} /></button>}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div>
                      <p className="text-xs uppercase tracking-[0.2em] text-pulse-muted mb-3">SoundCloud ({lang === 'es' ? 'preparado, no activo' : 'prepared, not active'})</p>
                      {[
                        { key: 'SOUNDCLOUD_CLIENT_ID', label: 'SOUNDCLOUD_CLIENT_ID', placeholder: 'future client id', field: 'soundcloud_id', isText: true },
                        { key: 'SOUNDCLOUD_CLIENT_SECRET', label: 'SOUNDCLOUD_CLIENT_SECRET', placeholder: 'future secret', field: 'soundcloud_secret' },
                        { key: 'SOUNDCLOUD_REDIRECT_URI', label: 'SOUNDCLOUD_REDIRECT_URI', placeholder: `${API}/api/verification/oauth/soundcloud/callback`, field: 'soundcloud_redirect', isText: true, readOnly: true },
                      ].map(item => (
                        <div key={item.key}>
                          <label className="text-xs text-pulse-muted block mb-1">{item.label}</label>
                          <div className="flex gap-2">
                            <input data-testid={`setting-${item.key.toLowerCase()}`} readOnly={item.readOnly} type={item.isText ? 'text' : (showKeys[item.field] ? 'text' : 'password')} value={item.readOnly ? `${(settingsForm.OAUTH_BASE_URL || window.location.origin).replace(/\/$/, '')}/api/verification/oauth/soundcloud/callback` : (settingsForm[item.key] || '')} onChange={e => setSettingsForm(f => ({ ...f, [item.key]: e.target.value }))} placeholder={item.placeholder} className={`flex-1 bg-pulse-surface border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none ${item.readOnly ? 'text-cyan-200 cursor-not-allowed' : ''}`} />
                            {!item.isText && <button data-testid={`toggle-${item.key.toLowerCase()}`} onClick={() => setShowKeys(k => ({ ...k, [item.field]: !k[item.field] }))} className="p-2 text-pulse-muted hover:text-white"><Eye size={16} /></button>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-pulse-border bg-pulse-elevated/60 p-4 space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-pulse-muted block mb-1">{lang === 'es' ? 'Umbral aprobar' : 'Approve threshold'}</label>
                      <input data-testid="verification-threshold-approved" type="number" value={settingsForm.song_verification_thresholds?.approved ?? 70} onChange={e => updateVerificationThreshold('approved', parseInt(e.target.value, 10) || 0)} className="w-full bg-pulse-surface border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                    </div>
                    <div>
                      <label className="text-xs text-pulse-muted block mb-1">{lang === 'es' ? 'Umbral revisión' : 'Review threshold'}</label>
                      <input data-testid="verification-threshold-review" type="number" value={settingsForm.song_verification_thresholds?.review ?? 40} onChange={e => updateVerificationThreshold('review', parseInt(e.target.value, 10) || 0)} className="w-full bg-pulse-surface border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                    </div>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-pulse-muted mb-3">{lang === 'es' ? 'Pesos del trust score' : 'Trust score weights'}</p>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {[
                        ['fingerprint_no_match', 'Fingerprint no match'],
                        ['fingerprint_match_found', 'Fingerprint match found'],
                        ['spotify_connected', 'Spotify linked'],
                        ['spotify_profile_match', 'Spotify profile match'],
                        ['spotify_catalog_match', 'Spotify catalog match'],
                        ['google_connected', 'Google linked'],
                        ['youtube_channel_match', 'YouTube channel match'],
                        ['youtube_catalog_match', 'YouTube catalog match'],
                        ['isrc_present', 'ISRC present'],
                        ['isrc_match', 'ISRC match'],
                        ['user_clean_history', 'Clean history'],
                        ['user_approved_history', 'Approved history'],
                      ].map(([key, label]) => (
                        <div key={key}>
                          <label className="text-[10px] text-pulse-muted block mb-1">{label}</label>
                          <input data-testid={`verification-weight-${key}`} type="number" value={settingsForm.song_verification_weights?.[key] ?? 0} onChange={e => updateVerificationWeight(key, parseInt(e.target.value, 10) || 0)} className="w-full bg-pulse-surface border border-pulse-border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Keys */}
              <div className="bg-pulse-surface border border-yellow-500/20 rounded-xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                  <CreditCard size={18} className="text-yellow-400" /> {lang === 'es' ? 'Pagos' : 'Payments'}
                </h3>
                <div className="space-y-4">
                  {[
                    { key: 'stripe_publishable_key', label: 'Stripe Publishable Key', placeholder: 'pk_live_...', field: 'stripe_pub' },
                    { key: 'stripe_secret_key', label: 'Stripe Secret Key', placeholder: 'sk_live_...', field: 'stripe_sec' },
                    { key: 'paypal_client_id', label: 'PayPal Client ID', placeholder: 'A...', field: 'paypal_id' },
                    { key: 'paypal_secret', label: 'PayPal Secret', placeholder: 'E...', field: 'paypal_sec' },
                  ].map(item => (
                    <div key={item.key}>
                      <label className="text-xs text-pulse-muted block mb-1">{item.label}</label>
                      <div className="flex gap-2">
                        <input data-testid={`setting-${item.key}`} type={showKeys[item.field] ? 'text' : 'password'} value={settingsForm[item.key] || ''} onChange={e => setSettingsForm(f => ({...f, [item.key]: e.target.value}))} placeholder={item.placeholder} className="flex-1 bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                        <button onClick={() => setShowKeys(k => ({...k, [item.field]: !k[item.field]}))} className="p-2 text-pulse-muted hover:text-white"><Eye size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Email Keys */}
              <div className="bg-pulse-surface border border-green-500/20 rounded-xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                  <Mail size={18} className="text-green-400" /> SendGrid (Email)
                </h3>
                <div className="space-y-4">
                  {[
                    { key: 'sendgrid_api_key', label: 'SendGrid API Key', placeholder: 'SG.xxxxx', field: 'sendgrid' },
                    { key: 'sendgrid_sender_email', label: 'Email remitente verificado', placeholder: 'info@pulsemusic.pro', field: 'sendgrid_sender', isText: true },
                  ].map(item => (
                    <div key={item.key}>
                      <label className="text-xs text-pulse-muted block mb-1">{item.label}</label>
                      <div className="flex gap-2">
                        <input data-testid={`setting-${item.key}`} type={item.isText ? 'text' : (showKeys[item.field] ? 'text' : 'password')} value={settingsForm[item.key] || ''} onChange={e => setSettingsForm(f => ({...f, [item.key]: e.target.value}))} placeholder={item.placeholder} className="flex-1 bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                        {!item.isText && <button onClick={() => setShowKeys(k => ({...k, [item.field]: !k[item.field]}))} className="p-2 text-pulse-muted hover:text-white"><Eye size={16} /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* LiveKit Keys */}
              <div className="bg-pulse-surface border border-red-500/20 rounded-xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                  <Radio size={18} className="text-red-400" /> LiveKit (Video Streaming)
                </h3>
                <div className="space-y-4">
                  {[
                    { key: 'livekit_api_key', label: 'LiveKit API Key', placeholder: 'API...', field: 'livekit_key' },
                    { key: 'livekit_api_secret', label: 'LiveKit API Secret', placeholder: 'secret...', field: 'livekit_secret' },
                    { key: 'livekit_url', label: 'LiveKit WebSocket URL', placeholder: 'wss://your-livekit.livekit.cloud', field: 'livekit_url', isText: true },
                  ].map(item => (
                    <div key={item.key}>
                      <label className="text-xs text-pulse-muted block mb-1">{item.label}</label>
                      <div className="flex gap-2">
                        <input data-testid={`setting-${item.key}`} type={item.isText ? 'text' : (showKeys[item.field] ? 'text' : 'password')} value={settingsForm[item.key] || ''} onChange={e => setSettingsForm(f => ({...f, [item.key]: e.target.value}))} placeholder={item.placeholder} className="flex-1 bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                        {!item.isText && <button onClick={() => setShowKeys(k => ({...k, [item.field]: !k[item.field]}))} className="p-2 text-pulse-muted hover:text-white"><Eye size={16} /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Monitoring & Security */}
              <div className="bg-pulse-surface border border-blue-500/20 rounded-xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                  <Activity size={18} className="text-blue-400" /> {lang === 'es' ? 'Monitoreo y Seguridad' : 'Monitoring & Security'}
                </h3>
                <div className="space-y-4">
                  {[
                    { key: 'google_analytics_id', label: 'Google Analytics ID', placeholder: 'G-XXXXXXXXXX', field: 'ga', isText: true },
                    { key: 'sentry_dsn', label: 'Sentry DSN', placeholder: 'https://xxx@sentry.io/xxx', field: 'sentry' },
                    { key: 'recaptcha_site_key', label: 'reCAPTCHA Site Key', placeholder: '6Le...', field: 'recaptcha_site', isText: true },
                    { key: 'recaptcha_secret_key', label: 'reCAPTCHA Secret Key', placeholder: '6Le...', field: 'recaptcha_secret' },
                  ].map(item => (
                    <div key={item.key}>
                      <label className="text-xs text-pulse-muted block mb-1">{item.label}</label>
                      <div className="flex gap-2">
                        <input data-testid={`setting-${item.key}`} type={item.isText ? 'text' : (showKeys[item.field] ? 'text' : 'password')} value={settingsForm[item.key] || ''} onChange={e => setSettingsForm(f => ({...f, [item.key]: e.target.value}))} placeholder={item.placeholder} className="flex-1 bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                        {!item.isText && <button onClick={() => setShowKeys(k => ({...k, [item.field]: !k[item.field]}))} className="p-2 text-pulse-muted hover:text-white"><Eye size={16} /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Director departments (QA handoff) */}
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-2 flex items-center gap-2">
                  <Building2 size={18} className="text-amber-400" /> {lang === 'es' ? 'Departamentos internos (directora)' : 'Internal departments (director)'}
                </h3>
                <p className="text-xs text-pulse-muted mb-4 max-w-xl">
                  {lang === 'es'
                    ? 'Activa solo cuando quieras usar la pestaña Direccion > Departamentos. Si esta desactivado, la API devuelve servicio no disponible para crear informes (503).'
                    : 'Turn on only when you want to use Direction > Departments. When off, the API returns unavailable for creating reports (503).'}
                </p>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="director-departments-enabled"
                    checked={Boolean(settingsForm.director_departments_enabled)}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, director_departments_enabled: e.target.checked }))}
                    className="w-5 h-5 rounded border-pulse-border"
                  />
                  <span className="text-sm">{lang === 'es' ? 'Modulo departamentos internos activo' : 'Internal departments module enabled'}</span>
                </label>
              </div>

              {/* Commission Rate */}
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-4 flex items-center gap-2">
                  <CreditCard size={18} className="text-yellow-400" /> {lang === 'es' ? 'Comision de Plataforma' : 'Platform Commission'}
                </h3>
                <div className="flex items-center gap-4">
                  <input data-testid="commission-rate" type="number" min="0" max="1" step="0.01" value={settingsForm.commission_rate ?? 0.30} onChange={e => setSettingsForm(f => ({...f, commission_rate: parseFloat(e.target.value)}))} className="w-32 bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  <span className="text-lg font-bold text-yellow-400">{((settingsForm.commission_rate ?? 0.30) * 100).toFixed(0)}%</span>
                </div>
              </div>

              {/* Emails */}
              <div className="bg-pulse-surface border border-pulse-border rounded-xl p-6">
                <h3 className="font-heading font-semibold text-lg mb-2 flex items-center gap-2">
                  <Mail size={18} className="text-pulse-primary" /> Emails
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-pulse-muted">{lang === 'es' ? 'Email principal / soporte' : 'Primary / support email'}</label>
                    <input data-testid="support-email-input" type="email" value={settingsForm.support_email || ''} onChange={e => setSettingsForm(f => ({...f, support_email: e.target.value}))} placeholder="info@pulsemusic.pro" className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  </div>
                  <div>
                    <label className="text-xs text-pulse-muted">{lang === 'es' ? 'Email de gestión' : 'Operations email'}</label>
                    <input data-testid="pluka-email-input" type="email" value={settingsForm.pluka_email || ''} onChange={e => setSettingsForm(f => ({...f, pluka_email: e.target.value}))} placeholder="alan.inca6@gmail.com" className="w-full bg-pulse-elevated border border-pulse-border rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-cyan-500 focus:outline-none" />
                  </div>
                </div>
              </div>

              <button data-testid="save-settings-btn" onClick={saveSettings} disabled={saving} className="bg-pulse-primary text-black font-bold px-8 py-3 rounded-xl hover:bg-pulse-primary-hover transition-colors duration-200 disabled:opacity-50 flex items-center gap-2">
                {saving ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <Save size={16} />}
                {t('common.save')}
              </button>
            </div>
          </div>
        )}
      </main>
      <GlobalOpsAssistant
        api={API}
        headers={headers}
        lang={lang}
        roleLabel={user?.role === 'admin' ? 'Dirección' : 'Pluka'}
        userRole={user?.role === 'pluka' ? 'pluka' : 'admin'}
      />
    </div>
  );
}
